import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./App.css";
import "./AppMobileAdmin.css";
import axios from "axios";
import { Toaster, ConfirmDialog, toast, smartToast, confirmAction } from "./feedback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant PWA Install Button - Installation automatique mobile
const PWAInstallButton = React.memo(() => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Détecter si c'est un mobile
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handler = (e) => {
      // Empêche l'affichage automatique du prompt
      e.preventDefault();
      // Stocke l'événement pour l'utiliser plus tard
      setDeferredPrompt(e);
      console.log('[PWA] beforeinstallprompt event captured for mobile');
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      console.log('[PWA] App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleInstallClick = async () => {
    // Vérifier si l'app est déjà installée
    if (window.matchMedia('(display-mode: standalone)').matches) {
      alert('✅ L\'application est déjà installée !');
      return;
    }

    if (isMobile) {
      console.log('[PWA] Mobile detected, attempting automatic installation');
      
      if (deferredPrompt) {
        try {
          // Installation automatique avec prompt natif
          console.log('[PWA] Triggering native install prompt');
          await deferredPrompt.prompt();
          
          const { outcome } = await deferredPrompt.userChoice;
          console.log('[PWA] Installation result:', outcome);
          
          if (outcome === 'accepted') {
            alert('🎉 Installation réussie ! MBeauty sera disponible sur votre écran d\'accueil.');
          } else {
            alert('❌ Installation annulée.');
          }
          
          setDeferredPrompt(null);
        } catch (error) {
          console.error('[PWA] Error during installation:', error);
          forceInstallOnMobile();
        }
      } else {
        // Pas d'événement natif disponible, forcer l'installation
        forceInstallOnMobile();
      }
    } else {
      // PC - Installation simple
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          alert('✅ Application installée !');
        }
        setDeferredPrompt(null);
      } else {
        alert('💻 Utilisez l\'icône d\'installation dans la barre d\'adresse de votre navigateur.');
      }
    }
  };

  const forceInstallOnMobile = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isAndroid) {
      // Android - Essayer de forcer l'installation
      console.log('[PWA] Forcing installation on Android');
      
      // Créer un événement beforeinstallprompt simulé
      const syntheticEvent = new Event('beforeinstallprompt');
      syntheticEvent.preventDefault = () => {};
      syntheticEvent.prompt = () => {
        // Tenter d'ouvrir directement le menu d'installation
        window.location.href = 'intent://install/#Intent;scheme=android-app;package=com.android.chrome;end';
        return Promise.resolve();
      };
      syntheticEvent.userChoice = Promise.resolve({ outcome: 'accepted' });
      
      // Déclencher l'événement
      window.dispatchEvent(syntheticEvent);
      
      // Fallback: Alert avec instructions simplifiées
      setTimeout(() => {
        alert('📱 Installation automatique...\n\nSi rien ne se passe :\n1. Menu du navigateur (⋮)\n2. "Ajouter à l\'écran d\'accueil"');
      }, 500);
      
    } else if (isIOS) {
      // iOS - Instructions directes mais simplifiées
      console.log('[PWA] iOS detected, showing streamlined instructions');
      alert('📱 Installation en cours...\n\n1. Touchez "Partager" (□↗) en bas\n2. "Sur l\'écran d\'accueil"\n3. "Ajouter"');
    } else {
      // Autre mobile
      alert('📱 Installation disponible via le menu de votre navigateur !');
    }
  };

  return (
    <button
      onClick={handleInstallClick}
      className="btn btn-outline btn-compact pwa-install-btn"
      title="Installer MBeauty comme application"
    >
      <span className="pwa-install-icon">📱</span>
      <span className="pwa-install-text">{isMobile ? 'Installer' : 'Installer l\'app'}</span>
    </button>
  );
});

// Booking Section - Composant séparé pour éviter la recréation à chaque render
const BookingSection = React.memo(({ 
  formData, 
  handleInputChange, 
  handleSubmitAppointment, 
  availableSlots,
  isLoggedIn,
  setCurrentSection,
  services,
  calculateTotalPrice,
  isSubmitting,
  urgency
}) => (
  <div className="container">
    <section className="booking">
      <div className="section-header">
        <h2>Réserver votre soin</h2>
        <p>Prenez rendez-vous en quelques clics</p>
      </div>

      {urgency && (urgency.week_left > 0 || urgency.next_7_days_left > 0) && (
        <div
          className={`urgency-banner ${urgency.week_left <= 3 ? 'urgency-banner--hot' : ''}`}
          data-testid="urgency-banner"
        >
          {urgency.week_left === 0 ? (
            <>
              <span className="urgency-icon">⏰</span>
              <span>
                <strong>Complet cette semaine.</strong> Il reste{' '}
                <strong>{urgency.next_7_days_left}</strong>{' '}
                créneau{urgency.next_7_days_left > 1 ? 'x' : ''} sur les 7 prochains jours.
              </span>
            </>
          ) : urgency.week_left <= 3 ? (
            <>
              <span className="urgency-icon">🔥</span>
              <span>
                Plus que <strong>{urgency.week_left}</strong> créneau{urgency.week_left > 1 ? 'x' : ''} disponible{urgency.week_left > 1 ? 's' : ''} cette semaine — <em>réservez vite !</em>
              </span>
            </>
          ) : (
            <>
              <span className="urgency-icon">📅</span>
              <span>
                <strong>{urgency.week_left}</strong> créneau{urgency.week_left > 1 ? 'x' : ''} restant{urgency.week_left > 1 ? 's' : ''} cette semaine.
              </span>
            </>
          )}
        </div>
      )}

      <div className="booking-container">
        {!isLoggedIn ? (
          <div className="auth-required-message">
            <p>Vous devez être connecté pour réserver un rendez-vous.</p>
            <button 
              onClick={() => setCurrentSection('client-login')}
              className="btn btn-primary"
            >
              Se connecter pour réserver
            </button>
          </div>
        ) : (
          <div className="booking-form">
          <form onSubmit={handleSubmitAppointment} className="form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Nom complet *</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="client_email"
                  value={formData.client_email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Téléphone *</label>
                <input
                  type="tel"
                  name="client_phone"
                  value={formData.client_phone}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Service (optionnel si commande sérum uniquement)</label>
              <select
                name="service_type"
                value={formData.service_type}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Pas de service (commande sérum uniquement)</option>
                <option value="lash_lift">Rehaussement de cils - 30€</option>
                <option value="lash_lift_tint">Rehaussement de cils + teinture - 35€</option>
                <option value="lash_lift_tint_botox">Rehaussement de cils + teinture + lash botox - 40€</option>
                <option value="browlift_simple">Browlift simple avec soins - 25€</option>
                <option value="browlift_tint">Browlift + teinture avec soins - 30€</option>
                <option value="brow_restruct_no_pluck">Restructuration sans épilation simple- 25€</option>
                <option value="brow_restruct_no_pluck_tint">Restructuration sans épilation + teinture - 30€</option>
                <option value="pack_lash_brow">Pack Regard Complet - 60€</option>
                <option value="pack_brow_complete">Pack Sourcils Expert - 50€</option>
                <option value="pack_rehauss_browlift_restruct">Pack Rehaussement + Browlift + Restructuration sans épilation (soins et teinture offerts) - 85€</option>
              </select>
            </div>

            {formData.service_type === 'pack_lash_brow' && (
              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="add_lash_botox"
                    checked={formData.add_lash_botox}
                    onChange={handleInputChange}
                    data-testid="booking-lash-botox-checkbox"
                  />
                  Ajouter lash botox (+5€)
                </label>
              </div>
            )}

            {formData.service_type === 'browlift_tint' && (
              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="add_hybrid_tint"
                    checked={formData.add_hybrid_tint}
                    onChange={handleInputChange}
                    data-testid="booking-hybrid-tint-checkbox"
                  />
                  Ajouter teinture hybride (+5€)
                </label>
              </div>
            )}

            {/* Option Sérum MbeautyCare - masquée si sérum uniquement */}
            {formData.service_type !== '' && (
              <div className="form-group serum-option">
                <label className="form-checkbox serum-checkbox">
                  <input
                    type="checkbox"
                    name="add_serum_mbeautycare"
                    checked={formData.add_serum_mbeautycare}
                    onChange={handleInputChange}
                  />
                  <div className="serum-details">
                    <span className="serum-title">Ajouter Sérum MbeautyCare (+15€)</span>
                    <span className="serum-desc">Sérum cils & sourcils 12ml - Favorise la pousse et la densité</span>
                  </div>
                </label>
              </div>
            )}

            {/* Affichage obligatoire du sérum pour commande sérum uniquement */}
            {formData.service_type === '' && (
              <div className="form-group serum-option">
                <div className="alert-info" style={{padding: '1rem', backgroundColor: '#f0f8ff', border: '1px solid #0066cc', borderRadius: '4px', marginBottom: '1rem'}}>
                  <strong>Commande sérum uniquement sélectionnée</strong>
                  <p style={{margin: '0.5rem 0 0 0', fontSize: '0.9rem'}}>Le sérum MbeautyCare sera automatiquement ajouté à votre commande.</p>
                </div>
              </div>
            )}

            {/* Adresse de livraison si sérum sélectionné OU si commande sérum uniquement */}
            {(formData.add_serum_mbeautycare || formData.service_type === '') && (
              <div className="form-group delivery-address">
                <label className="form-label">Adresse de livraison *</label>
                <textarea
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="form-textarea"
                  placeholder="Adresse complète pour la livraison du sérum..."
                />
              </div>
            )}

            {/* Sélection de créneaux seulement si un service est sélectionné */}
            {formData.service_type && (
              <div className="form-group">
                <label className="form-label">Créneau disponible *</label>
                <select
                  name="appointment_slot"
                  value={formData.appointment_date && formData.appointment_time ? `${formData.appointment_date}|${formData.appointment_time}` : ''}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                  data-testid="appointment-slot-select"
                >
                  <option value="">Sélectionnez un créneau</option>
                  {availableSlots
                    .filter(slot => slot.is_available)
                    .map(slot => {
                      const remainingForDate = urgency?.by_date?.[slot.date] ?? null;
                      const isLast = remainingForDate === 1;
                      return (
                        <option key={slot.id} value={`${slot.date}|${slot.time}`}>
                          {new Date(slot.date).toLocaleDateString('fr-FR')} à {slot.time}
                          {isLast ? ' — ⏰ dernière place ce jour-là' : ''}
                        </option>
                      );
                    })}
                </select>
                {urgency?.by_date && formData.appointment_date && urgency.by_date[formData.appointment_date] === 1 && (
                  <div className="slot-last-place-hint" data-testid="last-place-hint">
                    ⏰ Il ne reste qu'une place à cette date. Réservez maintenant.
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes particulières</label>
              <textarea
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleInputChange}
                rows="3"
                className="form-textarea"
                placeholder="Allergies, préférences particulières..."
              />
            </div>

            {/* Récapitulatif du prix */}
            {(formData.service_type || formData.add_serum_mbeautycare || formData.service_type === '') && (
              <div className="booking-summary">
                <h4>Récapitulatif</h4>
                <div className="summary-details">
                  {formData.service_type && services[formData.service_type] && (
                    <div className="summary-line">
                      <span>Service: {services[formData.service_type].name}</span>
                      <span>{services[formData.service_type].price}€</span>
                    </div>
                  )}
                  {formData.add_lash_botox && formData.service_type === 'pack_lash_brow' && (
                    <div className="summary-line">
                      <span>Lash Botox</span>
                      <span>+5€</span>
                    </div>
                  )}
                  {formData.add_hybrid_tint && formData.service_type === 'browlift_tint' && (
                    <div className="summary-line">
                      <span>Teinture hybride</span>
                      <span>+5€</span>
                    </div>
                  )}
                  {(formData.add_serum_mbeautycare || formData.service_type === '') && (
                    <div className="summary-line">
                      <span>Sérum MbeautyCare (12ml)</span>
                      <span>{formData.service_type === '' ? '15€' : '+15€'}</span>
                    </div>
                  )}
                  <div className="summary-total">
                    <strong>
                      <span>Total:</span>
                      <span>{calculateTotalPrice}€</span>
                    </strong>
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitting} data-testid="booking-submit-btn">
                {isSubmitting
                  ? 'Traitement en cours…'
                  : (formData.service_type ? 'Confirmer le rendez-vous' : 'Confirmer la commande')}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </section>
  </div>
));

// Reviews Section - Composant séparé avec nouveau design élégant
const ReviewsSection = React.memo(({
  reviewData,
  handleReviewChange,
  handleReviewSubmit,
  reviews,
  services,
  isLoggedIn,
  setCurrentSection
}) => {
  // Fonction pour formatter la date relative
  const getTimeAgo = (createdAt) => {
    const now = new Date();
    const reviewDate = new Date(createdAt);
    const diffTime = Math.abs(now - reviewDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 7) {
      return "Il y a quelques jours";
    } else if (diffDays <= 14) {
      return "Il y a 2 semaines";
    } else if (diffDays <= 30) {
      return "Il y a 3 semaines";
    } else if (diffDays <= 60) {
      return "Il y a 1 mois";
    } else if (diffDays <= 90) {
      return "Il y a 2 mois";
    } else {
      return "Il y a 3 semaines";
    }
  };

  // Fonction pour formatter le nom du service
  const getServiceDisplayName = (serviceType) => {
    const serviceNames = {
      'lash_lift': 'Rehaussement de cils',
      'lash_lift_tint': 'Rehaussement de cils + teinture',
      'lash_lift_tint_botox': 'Rehaussement de cils + teinture + lash botox',
      'browlift_simple': 'Browlift simple',
      'browlift_tint': 'Browlift + teinture avec soins',
      'brow_restruct_no_pluck': 'Restructuration sans épilation simple',
      'brow_restruct_no_pluck_tint': 'Restructuration sourcils',
      'pack_lash_brow': 'Pack Regard Complet',
      'pack_brow_complete': 'Pack Sourcils Expert',
      'pack_rehauss_browlift_restruct': 'Pack Rehaussement + Browlift + Restructuration'
    };
    return serviceNames[serviceType] || serviceType;
  };

  return (
    <div className="container">
      <section className="reviews">
        <div className="section-header">
          <h2>Avis Clients</h2>
          <p>Ce que pensent nos clientes de nos services</p>
        </div>
        
        <div className="reviews-grid-new">
          {reviews.map((review) => (
            <div key={review.id} className="review-card-new">
              <div className="review-header-new">
                <div className="review-client-info">
                  <h4 className="review-client-name">{review.client_name}</h4>
                  <div className="review-verified-badge">Vérifié</div>
                </div>
                <div className="review-service-name">
                  {review.service_type && getServiceDisplayName(review.service_type)}
                </div>
              </div>

              <div className="review-rating-section">
                <div className="review-stars-new">
                  {[...Array(5)].map((_, index) => (
                    <span 
                      key={index} 
                      className={`star-new ${index < review.rating ? 'filled' : 'empty'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <div className="review-time-ago">
                  {getTimeAgo(review.created_at)}
                </div>
              </div>

              <div className="review-comment-new">
                <p>"{review.comment}"</p>
              </div>
            </div>
          ))}
        </div>

        <div className="review-form-section">
          <h3>Laisser un avis</h3>
          
          {!isLoggedIn ? (
            <div className="auth-required-message">
              <p>Vous devez être connecté pour laisser un avis.</p>
              <button 
                onClick={() => setCurrentSection('client-login')}
                className="btn btn-primary"
              >
                Se connecter pour laisser un avis
              </button>
            </div>
          ) : (
            <form onSubmit={handleReviewSubmit} className="form">
              <div className="form-group">
                <label className="form-label">Note *</label>
                <select
                  name="rating"
                  value={reviewData.rating}
                  onChange={handleReviewChange}
                  required
                  className="form-select"
                >
                  <option value={5}>5 étoiles - Excellent</option>
                  <option value={4}>4 étoiles - Très bien</option>
                  <option value={3}>3 étoiles - Bien</option>
                  <option value={2}>2 étoiles - Moyen</option>
                  <option value={1}>1 étoile - Décevant</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Service (optionnel)</label>
                <select
                  name="service_type"
                  value={reviewData.service_type}
                  onChange={handleReviewChange}
                  className="form-select">
                  <option value="serum-15">Sérum pousse cils et sourcils</option>
                  <option value="lash_lift">Rehaussement de cils</option>
                  <option value="lash_lift_tint">Rehaussement de cils + teinture</option>
                  <option value="lash_lift_tint_botox">Rehaussement de cils + teinture + lash botox</option>
                  <option value="browlift_simple">Browlift simple avec soins</option>
                  <option value="browlift_tint">Browlift + teinture avec soins</option>
                  <option value="brow_restruct_no_pluck">Restructuration sans épilation simple</option>
                  <option value="brow_restruct_no_pluck_tint">Restructuration sans épilation + teinture</option>
                  <option value="pack_lash_brow">Pack Regard Complet</option>
                  <option value="pack_brow_complete">Pack Sourcils Expert</option>
                  <option value="pack_rehauss_browlift_restruct">Pack Rehaussement + Browlift + Restructuration</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Votre avis *</label>
                <textarea
                  name="comment"
                  value={reviewData.comment}
                  onChange={handleReviewChange}
                  required
                  rows="4"
                  className="form-textarea"
                  placeholder="Partagez votre expérience..."
                />
              </div>

              <button type="submit" className="btn btn-primary btn-lg" style={{width: '100%'}}>
                Publier mon avis
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
});

// Client Login Section - Composant séparé avec gestion login/signup/forgot password
const ClientLoginSection = React.memo(({
  loginData,
  registerData,
  forgotPasswordData,
  resetPasswordData,
  handleLoginChange,
  handleRegisterChange,
  handleForgotPasswordChange,
  handleResetPasswordChange,
  handleLoginSubmit,
  handleRegisterSubmit,
  handleForgotPasswordSubmit,
  handleResetPasswordSubmit,
  showLogin,
  setShowLogin,
  showForgotPassword,
  setShowForgotPassword,
  showResetPassword,
  setShowResetPassword
}) => (
  <div className="client-auth">
    <div className="auth-container">
      {/* Navigation tabs - only show when not in forgot/reset password modes */}
      {!showForgotPassword && !showResetPassword && (
        <div className="auth-toggle">
          <button 
            className={showLogin ? 'auth-tab active' : 'auth-tab'}
            onClick={() => setShowLogin(true)}
          >
            Connexion
          </button>
          <button 
            className={!showLogin ? 'auth-tab active' : 'auth-tab'}
            onClick={() => setShowLogin(false)}
          >
            Inscription
          </button>
        </div>
      )}
      
      {/* Login Form */}
      {showLogin && !showForgotPassword && !showResetPassword && (
        <div>
          <h2>Connexion</h2>
          <form onSubmit={handleLoginSubmit} className="form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={loginData.email}
                onChange={handleLoginChange}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Mot de passe</label>
              <input
                type="password"
                name="password"
                value={loginData.password}
                onChange={handleLoginChange}
                required
                className="form-input"
              />
            </div>
            
            {/* Forgot Password Link */}
            <div className="form-group" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setShowLogin(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666666',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '300'
                }}
              >
                Mot de passe oublié ?
              </button>
            </div>

            <button type="submit" className="btn btn-primary btn-lg" style={{width: '100%'}}>
              Se connecter
            </button>
          </form>
        </div>
      )}

      {/* Register Form */}
      {!showLogin && !showForgotPassword && !showResetPassword && (
        <div>
          <h2>Inscription</h2>
          <form onSubmit={handleRegisterSubmit} className="form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Prénom</label>
                <input
                  type="text"
                  name="first_name"
                  value={registerData.first_name}
                  onChange={handleRegisterChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nom</label>
                <input
                  type="text"
                  name="last_name"
                  value={registerData.last_name}
                  onChange={handleRegisterChange}
                  required
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={registerData.email}
                onChange={handleRegisterChange}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Téléphone</label>
              <input
                type="tel"
                name="phone"
                value={registerData.phone}
                onChange={handleRegisterChange}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Mot de passe</label>
              <input
                type="password"
                name="password"
                value={registerData.password}
                onChange={handleRegisterChange}
                required
                className="form-input"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{width: '100%'}}>
              Créer mon compte
            </button>
          </form>
        </div>
      )}

      {/* Forgot Password Form */}
      {showForgotPassword && !showResetPassword && (
        <div>
          <h2>Mot de passe oublié</h2>
          <p style={{ marginBottom: '2rem', color: '#666666', fontWeight: '300' }}>
            Entrez votre adresse email pour recevoir un code de réinitialisation.
          </p>
          <form onSubmit={handleForgotPasswordSubmit} className="form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={forgotPasswordData.email}
                onChange={handleForgotPasswordChange}
                required
                className="form-input"
                placeholder="votre.email@exemple.com"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{width: '100%'}}>
              Envoyer le code
            </button>
          </form>
          
          {/* Back to login link */}
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setShowLogin(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#666666',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '300'
              }}
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      )}

      {/* Reset Password Form */}
      {showResetPassword && (
        <div>
          <h2>Réinitialiser le mot de passe</h2>
          <p style={{ marginBottom: '2rem', color: '#666666', fontWeight: '300' }}>
            Entrez le code reçu par email et votre nouveau mot de passe.
          </p>
          <form onSubmit={handleResetPasswordSubmit} className="form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={resetPasswordData.email}
                onChange={handleResetPasswordChange}
                required
                className="form-input"
                readOnly
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Code de réinitialisation</label>
              <input
                type="text"
                name="code"
                value={resetPasswordData.code}
                onChange={handleResetPasswordChange}
                required
                className="form-input"
                placeholder="123456"
                maxLength="6"
                pattern="[0-9]{6}"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Nouveau mot de passe</label>
              <input
                type="password"
                name="new_password"
                value={resetPasswordData.new_password}
                onChange={handleResetPasswordChange}
                required
                className="form-input"
                minLength="6"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                name="confirm_password"
                value={resetPasswordData.confirm_password}
                onChange={handleResetPasswordChange}
                required
                className="form-input"
                minLength="6"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{width: '100%'}}>
              Réinitialiser le mot de passe
            </button>
          </form>
          
          {/* Back to login link */}
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={() => {
                setShowResetPassword(false);
                setShowLogin(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#666666',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '300'
              }}
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
));

const App = () => {
  const [currentSection, setCurrentSection] = useState('home');
  const [services, setServices] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [updatingAppointment, setUpdatingAppointment] = useState(null);
  const [loadingServices, setLoadingServices] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [urgency, setUrgency] = useState(null); // { week_left, next_7_days_left, by_date, total_future }
  const [newSlot, setNewSlot] = useState({
    date: '',
    time: '',
    is_available: true
  });

  // Client authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [clientAppointments, setClientAppointments] = useState([]);
  const [clientOrders, setClientOrders] = useState([]);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: ''
  });
  const [showLogin, setShowLogin] = useState(true);

  // Password reset states
  const [forgotPasswordForm, setForgotPasswordForm] = useState({ email: '' });
  const [resetPasswordForm, setResetPasswordForm] = useState({ 
    email: '', code: '', new_password: '', confirm_password: '' 
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Admin email settings removed - no longer needed

  // NOUVEAU: État pour la gestion des reviews en attente
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);

  // NOUVEAU: État pour les onglets admin
  const [adminActiveTab, setAdminActiveTab] = useState('dashboard');
  
  // NOUVEAU: État pour les filtres de rendez-vous
  const [appointmentFilter, setAppointmentFilter] = useState('all');

  // État pour la sélection de semaine du planning
  const [selectedWeekMonday, setSelectedWeekMonday] = useState(() => {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  // État pour le mode de planning (semaine ou mois)
  const [planningMode, setPlanningMode] = useState('week'); // 'week' ou 'month'
  
  // État pour la sélection du mois
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Form states - États remontés dans App.js pour éviter les re-renders
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    service_type: 'lash_lift',
    add_lash_botox: false,
    add_hybrid_tint: false, // Teinture hybride (+5€) pour browlift + teinture
    add_serum_mbeautycare: false, // New field for serum option
    appointment_date: '',
    appointment_time: '',
    additional_notes: '',
    delivery_address: '' // New field for delivery address
  });

  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: '',
    service_type: ''
  });

  // État de soumission de la réservation (feedback pro)
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  // Handlers optimisés avec useCallback pour éviter les re-renders
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle appointment slot selection specially
    if (name === 'appointment_slot' && value) {
      const [date, time] = value.split('|');
      setFormData(prev => ({
        ...prev,
        appointment_date: date,
        appointment_time: time
      }));
      return;
    }
    
    // Handle service type change specially - if empty service, force serum to true
    if (name === 'service_type') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        add_serum_mbeautycare: value === '' ? true : prev.add_serum_mbeautycare,
        add_hybrid_tint: value === 'browlift_tint' ? prev.add_hybrid_tint : false,
        add_lash_botox: value === 'pack_lash_brow' ? prev.add_lash_botox : false
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleLoginInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleRegisterInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setRegisterForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleForgotPasswordInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setForgotPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleResetPasswordInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setResetPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleReviewInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Email settings handler removed - no longer needed

  const handleNewSlotChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewSlot(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Remplace les alert() natifs par des toasts élégants (messages pro)
  useEffect(() => {
    const nativeAlert = window.alert;
    window.alert = (msg) => smartToast(msg);
    return () => {
      window.alert = nativeAlert;
    };
  }, []);

  // Check if user is logged in on app start
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetchCurrentClient(token);
    }
  }, []);

  // NOUVEAU: Pré-remplir le formulaire avec les informations du client connecté
  useEffect(() => {
    if (isLoggedIn && currentClient) {
      setFormData(prev => ({
        ...prev,
        client_name: `${currentClient.first_name} ${currentClient.last_name}`,
        client_email: currentClient.email,
        client_phone: currentClient.phone || ''
      }));
    }
  }, [isLoggedIn, currentClient]);

  const fetchCurrentClient = async (token) => {
    try {
      const response = await axios.get(`${API}/clients/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentClient(response.data);
      setIsLoggedIn(true);
      fetchClientAppointments(token);
      
      // If admin, fetch admin data
      if (response.data.is_admin) {
        fetchAdminData(token);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
      localStorage.removeItem('authToken');
      setIsLoggedIn(false);
      setCurrentClient(null);
    }
  };

  const fetchClientAppointments = async (token) => {
    try {
      const response = await axios.get(`${API}/clients/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientAppointments(response.data);
    } catch (error) {
      console.error('Error fetching client appointments:', error);
    }
  };

  // OPTIMISATION: Fonction d'actualisation dynamique avec appels parallèles
  const refreshAdminData = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (token && currentClient?.is_admin) {
      try {
        // OPTIMISATION: Exécuter tous les appels de refresh en parallèle
        await Promise.all([
          fetchAdminData(token),
          fetchAvailableSlots(),
          fetchReviews()
        ]);
        console.log('✅ Données admin actualisées');
      } catch (error) {
        console.error('❌ Erreur lors de l\'actualisation des données admin:', error);
      }
    }
  }, [currentClient?.is_admin]);

    

  // OPTIMISATION: Fonction d'actualisation des données client avec appels parallèles
  const refreshClientData = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (token && isLoggedIn) {
      try {
        // Exécuter les appels client en parallèle
        const [, ordersResult] = await Promise.all([
          fetchClientAppointments(token),
          axios.get(`${API}/clients/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(e => {
            console.error('Error fetching client orders:', e);
            return { data: [] };
          })
        ]);
        
        setClientOrders(ordersResult.data);
      } catch (error) {
        console.error('Error refreshing client data:', error);
      }
    }
  }, [isLoggedIn]);

  const fetchAdminData = async (token) => {
    try {
      setLoadingAppointments(true);
      
      // OPTIMISATION: Exécuter tous les appels admin en parallèle
      const [appointmentsResponse, pendingReviewsResponse, allReviewsResponse] = await Promise.all([
        axios.get(`${API}/admin/appointments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/reviews/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/reviews`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Mettre à jour tous les états en une fois
      setAppointments(appointmentsResponse.data);
      setPendingReviews(pendingReviewsResponse.data);
      setAllReviews(allReviewsResponse.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/clients/login`, loginForm);
      localStorage.setItem('authToken', response.data.token);
      setCurrentClient(response.data.client);
      setIsLoggedIn(true);
      
      // Redirect based on user type
      if (response.data.client.is_admin) {
        setCurrentSection('admin-area');
        fetchAdminData(response.data.token);
      } else {
        setCurrentSection('client-area');
        fetchClientAppointments(response.data.token);
      }
      
      setLoginForm({ email: '', password: '' });
    } catch (error) {
      console.error('Login error:', error);
      alert('Email ou mot de passe incorrect');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/clients/register`, registerForm);
      localStorage.setItem('authToken', response.data.token);
      setCurrentClient(response.data.client);
      setIsLoggedIn(true);
      setCurrentSection('client-area');
      fetchClientAppointments(response.data.token);
      setRegisterForm({ email: '', password: '', first_name: '', last_name: '', phone: '' });
    } catch (error) {
      console.error('Register error:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création du compte');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients/forgot-password`, forgotPasswordForm);
      alert('Si l\'email existe, un code de réinitialisation a été envoyé à votre adresse email.');
      setResetPasswordForm(prev => ({ ...prev, email: forgotPasswordForm.email }));
      setShowForgotPassword(false);
      setShowResetPassword(true);
      setForgotPasswordForm({ email: '' });
    } catch (error) {
      console.error('Forgot password error:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetPasswordForm.new_password !== resetPasswordForm.confirm_password) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (resetPasswordForm.new_password.length < 6) {
      alert('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await axios.post(`${API}/clients/reset-password`, {
        email: resetPasswordForm.email,
        code: resetPasswordForm.code,
        new_password: resetPasswordForm.new_password
      });
      alert('Mot de passe réinitialisé avec succès ! Vous pouvez maintenant vous connecter.');
      
      // Reset all forms and return to login
      setResetPasswordForm({ email: '', code: '', new_password: '', confirm_password: '' });
      setShowResetPassword(false);
      setShowLogin(true);
    } catch (error) {
      console.error('Reset password error:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    setCurrentClient(null);
    setClientAppointments([]);
    setAppointments([]);
    setCurrentSection('home');
  };

  const fetchAvailableSlots = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/available-slots`);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux:', error);
    }
  }, []);

  const fetchUrgency = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/availability/urgency`);
      setUrgency(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données de disponibilité:', error);
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    }
  }, []);

  const fetchFAQs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des FAQs:', error);
    }
  }, []);

  useEffect(() => {
    // OPTIMISATION: Regrouper TOUS les appels de données initiales en parallèle
    const loadInitialData = async () => {
      try {
        setLoadingServices(true);
        
        // Exécuter tous les appels de données publiques en parallèle
        const [servicesResponse] = await Promise.all([
          axios.get(`${API}/services`),
          fetchAvailableSlots(),
          fetchReviews(),
          fetchFAQs(),
          fetchUrgency()
        ]);
        
        setServices(servicesResponse.data);
      } catch (error) {
        console.error('Erreur lors du chargement des données initiales:', error);
      } finally {
        setLoadingServices(false);
      }
    };
    
    loadInitialData();
  }, [fetchAvailableSlots, fetchReviews, fetchFAQs, fetchUrgency]);

  // Review submission avec authentification
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!isLoggedIn) {
      alert('Vous devez être connecté pour laisser un avis.');
      setCurrentSection('client-login');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API}/reviews`, reviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Merci pour votre avis ! Il sera publié après validation.');
      setReviewForm({
        rating: 5,
        comment: '',
        service_type: ''
      });
      
      // Refresh reviews
      fetchReviews();
      // Si admin, actualiser aussi les données admin
      if (currentClient?.is_admin) {
        refreshAdminData();
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'avis:', error);
      if (error.response?.status === 401) {
        alert('Votre session a expiré. Veuillez vous reconnecter.');
        setCurrentSection('client-login');
      } else {
        alert('Erreur lors de l\'envoi de l\'avis');
      }
    }
  };

  // Fonction pour calculer le prix total
  const calculateTotalPrice = useMemo(() => {
    let total = 0;
    
    // Prix du service si sélectionné
    if (formData.service_type && services[formData.service_type]) {
      total += services[formData.service_type].price;
      
      // Ajouter le prix du lash botox si applicable
      if (formData.add_lash_botox && formData.service_type === 'pack_lash_brow') {
        total += 5;
      }
      
      // Ajouter le prix de la teinture hybride si applicable
      if (formData.add_hybrid_tint && formData.service_type === 'browlift_tint') {
        total += 5;
      }
    }
    
    // Ajouter le prix du sérum si sélectionné OU si commande sérum uniquement
    if (formData.add_serum_mbeautycare || formData.service_type === '') {
      total += 15;
    }
    
    return total;
  }, [formData.service_type, formData.add_lash_botox, formData.add_hybrid_tint, formData.add_serum_mbeautycare, services]);

  const handleSubmitAppointment = async (e) => {
    e.preventDefault();
    
    // Validation: Le formulaire est valide si on a un service OU si on est en mode sérum uniquement (service_type === '')
    // Pas besoin de validation ici car l'utilisateur ne peut que sélectionner des options valides
    
    // Validation: Si sérum sélectionné OU commande sérum uniquement, l'adresse est obligatoire
    if ((formData.add_serum_mbeautycare || formData.service_type === '') && !formData.delivery_address.trim()) {
      alert('L\'adresse de livraison est obligatoire pour la commande du sérum.');
      return;
    }
    
    // Validation: Si service sélectionné, date et heure sont obligatoires
    if (formData.service_type && (!formData.appointment_date || !formData.appointment_time)) {
      alert('La date et l\'heure sont obligatoires pour un rendez-vous service.');
      return;
    }
    
    setBookingSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Si c'est sérum uniquement (pas de service), créer un rendez-vous avec sérum
      if (formData.service_type === '') {
        // Pour sérum uniquement, créer un rendez-vous avec add_serum_mbeautycare = true
        const appointmentData = {
          ...formData,
          service_type: null, // Pas de service
          add_serum_mbeautycare: true, // Forcer sérum à true
          appointment_date: new Date().toISOString().split('T')[0], // Date par défaut
          appointment_time: '00:00' // Heure par défaut
        };
        
        await axios.post(`${API}/appointments`, appointmentData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        alert(`Commande créée avec succès !
Produit: Sérum MbeautyCare
Prix: 15€`);
        
        // Actualiser les rendez-vous du client
        fetchClientAppointments(token);
      } else {
        // Préparer les données pour un rendez-vous (avec ou sans sérum supplémentaire)
        const appointmentData = {
          ...formData,
          // Si pas de service sélectionné, on met une date/heure par défaut pour éviter les erreurs backend
          appointment_date: formData.service_type ? formData.appointment_date : new Date().toISOString().split('T')[0],
          appointment_time: formData.service_type ? formData.appointment_time : '00:00'
        };
        
        await axios.post(`${API}/appointments`, appointmentData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const serviceType = formData.service_type || 'serum_only';
        const serviceName = services[serviceType]?.name || 'Commande sérum uniquement';
        
        alert(`${formData.service_type ? 'Rendez-vous' : 'Commande'} créé avec succès ! 
Service: ${serviceName}
${formData.add_serum_mbeautycare ? 'Sérum MbeautyCare inclus' : ''}
Prix total: ${calculateTotalPrice}€`);
        
        // Actualiser les rendez-vous du client
        if (currentClient) {
          fetchClientAppointments(token);
        }
      }
      
      // Reset du formulaire avec les données client pré-remplies si connecté
      setFormData({
        client_name: isLoggedIn && currentClient ? `${currentClient.first_name} ${currentClient.last_name}` : '',
        client_email: isLoggedIn && currentClient ? currentClient.email : '',
        client_phone: isLoggedIn && currentClient ? currentClient.phone || '' : '',
        service_type: 'lash_lift', // Retour à la valeur par défaut
        add_lash_botox: false,
        add_hybrid_tint: false,
        add_serum_mbeautycare: false,
        appointment_date: '',
        appointment_time: '',
        additional_notes: '',
        delivery_address: ''
      });
      fetchAvailableSlots();
      fetchUrgency();
      if (currentClient?.is_admin) {
        refreshAdminData();
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      if (error.response?.status === 401) {
        alert('Votre session a expiré. Veuillez vous reconnecter.');
        setCurrentSection('client-login');
      } else {
        // Mieux gérer l'affichage des erreurs pour éviter [object Object]
        let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';
        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (typeof error.response.data.detail === 'object') {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        }
        alert(errorMessage);
      }
    } finally {
      setBookingSubmitting(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    if (updatingAppointment === appointmentId) return;
    try {
      setUpdatingAppointment(appointmentId);
      const token = localStorage.getItem('authToken');
      
      await axios.put(`${API}/admin/appointments/${appointmentId}/status`, null, {
        params: { status: newStatus },
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state FIRST pour réactivité immédiate
      setAppointments(prev => 
        prev.map(appointment => 
          appointment.id === appointmentId 
            ? { ...appointment, status: newStatus }
            : appointment
        )
      );

      const statusText = newStatus === 'confirmed' ? 'confirmé' : 'annulé';
      alert(`Rendez-vous ${statusText} avec succès!`);
      
      // Actualiser SEULEMENT les créneaux et données client (pas tout le refresh admin)
      await Promise.all([
        fetchAvailableSlots(),
        refreshClientData()
      ]);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingAppointment(null);
    }
  };

  const addTimeSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.date || !newSlot.time) {
      alert('Veuillez remplir tous les champs');
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API}/admin/available-slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewSlot({ date: '', time: '', is_available: true });
      await fetchAvailableSlots();
      alert('Créneau ajouté avec succès!');
    } catch (error) {
      console.error('Erreur lors de l\'ajout du créneau:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'ajout du créneau');
    }
  };

  const deleteTimeSlot = async (slotId) => {
    const ok = await confirmAction({
      title: 'Supprimer le créneau',
      message: 'Êtes-vous sûr de vouloir supprimer ce créneau ?',
      confirmText: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API}/admin/available-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableSlots(prev => prev.filter(slot => slot.id !== slotId));
      alert('Créneau supprimé avec succès!');
    } catch (error) {
      console.error('Erreur lors de la suppression du créneau:', error);
      alert('Erreur lors de la suppression du créneau');
    }
  };

  // Email settings update function removed - no longer needed

  // Gestion des reviews en attente
  const approveReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API}/admin/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Avis approuvé avec succès!');
      // Rafraîchir les données
      refreshAdminData();
      fetchReviews(); // Rafraîchir aussi les reviews publiques
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
      alert('Erreur lors de l\'approbation de l\'avis');
    }
  };

  const rejectReview = async (reviewId) => {
    const ok = await confirmAction({
      title: 'Rejeter cet avis',
      message: 'Êtes-vous sûr de vouloir rejeter et supprimer cet avis ?',
      confirmText: 'Rejeter',
      danger: true,
    });
    if (ok) {
      try {
        const token = localStorage.getItem('authToken');
        await axios.put(`${API}/admin/reviews/${reviewId}/reject`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Avis rejeté et supprimé avec succès!');
        // Rafraîchir les données
        refreshAdminData();
      } catch (error) {
        console.error('Erreur lors du rejet:', error);
        alert('Erreur lors du rejet de l\'avis');
      }
    }
  };

  // Supprimer les rendez-vous et créneaux passés depuis plus de 24H + tous les RDV annulés
  const deleteCompletedAppointments = async () => {
    const ok = await confirmAction({
      title: 'Nettoyer le planning',
      message: 'Supprimer tous les créneaux et rendez-vous passés depuis plus de 24H, ainsi que tous les rendez-vous annulés ?',
      confirmText: 'Tout supprimer',
      danger: true,
    });
    if (!ok) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const now = new Date(); // Date et heure actuelles précises
      const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000)); // 24H avant maintenant
      
      // Filtrer les rendez-vous à supprimer :
      //  - tous les RDV annulés (quelle que soit leur date)
      //  - les RDV passés depuis plus de 24H
      //  - les commandes sérum marquées "completed"
      const pastAppointments = appointments.filter(apt => {
        // Tous les RDV annulés (indépendamment de la date)
        if (apt.status === 'cancelled') {
          return true;
        }

        // Commandes sérum sans date de RDV réelle (produit uniquement)
        if (!apt.appointment_date || apt.appointment_date === '1970-01-01') {
          return apt.status === 'completed';
        }
        
        // Pour les RDV avec service, vérifier date + heure précise
        if (apt.appointment_date && apt.appointment_time) {
          // Construire la date complète du créneau (date + heure)
          const [hours, minutes] = apt.appointment_time.split(':');
          const appointmentDateTime = new Date(apt.appointment_date);
          appointmentDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          
          // Le RDV est passé depuis plus de 24H
          const isOlderThan24H = appointmentDateTime < twentyFourHoursAgo;
          
          console.log(`RDV ${apt.id}: ${apt.appointment_date} ${apt.appointment_time} - ${isOlderThan24H ? 'PASSÉ +24H' : 'RÉCENT'} (limite: ${twentyFourHoursAgo.toLocaleString('fr-FR')})`);
          
          return isOlderThan24H;
        }
        
        return false;
      });

      // Filtrer les créneaux passés depuis plus de 24H (disponibles ET indisponibles)
      const pastSlots = availableSlots.filter(slot => {
        if (slot.date && slot.time) {
          // Construire la date complète du créneau (date + heure)
          const [hours, minutes] = slot.time.split(':');
          const slotDateTime = new Date(slot.date);
          slotDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          
          // Le créneau est passé depuis plus de 24H
          const isOlderThan24H = slotDateTime < twentyFourHoursAgo;
          
          console.log(`Créneau ${slot.id}: ${slot.date} ${slot.time} - ${isOlderThan24H ? 'PASSÉ +24H' : 'RÉCENT'} (limite: ${twentyFourHoursAgo.toLocaleString('fr-FR')})`);
          
          return isOlderThan24H;
        }
        return false;
      });

      if (pastAppointments.length === 0 && pastSlots.length === 0) {
        alert('Aucun rendez-vous à nettoyer (aucun passé +24H, aucun annulé, aucun créneau expiré).');
        return;
      }

      console.log(`Suppression de ${pastAppointments.length} rendez-vous et ${pastSlots.length} créneaux passés:`);
      console.log('RDV:', pastAppointments.map(apt => ({ id: apt.id, date: apt.appointment_date, time: apt.appointment_time })));
      console.log('Créneaux:', pastSlots.map(slot => ({ id: slot.id, date: slot.date, time: slot.time })));

      // Supprimer chaque rendez-vous passé
      let deletedAppointmentsCount = 0;
      for (const appointment of pastAppointments) {
        try {
          await axios.delete(`${API}/admin/appointments/${appointment.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedAppointmentsCount++;
        } catch (deleteError) {
          console.error(`Erreur lors de la suppression du RDV ${appointment.id}:`, deleteError);
        }
      }

      // Supprimer chaque créneau passé
      let deletedSlotsCount = 0;
      for (const slot of pastSlots) {
        try {
          await axios.delete(`${API}/admin/available-slots/${slot.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedSlotsCount++;
        } catch (deleteError) {
          console.error(`Erreur lors de la suppression du créneau ${slot.id}:`, deleteError);
        }
      }

      const totalDeleted = deletedAppointmentsCount + deletedSlotsCount;
      if (totalDeleted > 0) {
        alert(`${deletedAppointmentsCount} rendez-vous et ${deletedSlotsCount} créneaux passés supprimés avec succès!`);
        await refreshAdminData();
      } else {
        alert('Aucun élément n\'a pu être supprimé.');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression des éléments passés:', error);
      alert('Erreur lors de la suppression: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Statistiques en temps réel
  const adminStats = useMemo(() => {
    const pendingAppointments = appointments.filter(a => a.status === 'pending').length;
    const confirmedAppointments = appointments.filter(a => a.status === 'confirmed').length;
    const pendingReviewsCount = pendingReviews.length;
    const totalAppointments = appointments.length;
    
    return {
      total: totalAppointments,
      pending: pendingAppointments,
      confirmed: confirmedAppointments,
      pendingReviews: pendingReviewsCount
    };
  }, [appointments, pendingReviews]);

  // NOUVEAU: Filtrage des rendez-vous selon le statut sélectionné
  const filteredAppointments = useMemo(() => {
    if (appointmentFilter === 'all') return appointments;
    return appointments.filter(appointment => appointment.status === appointmentFilter);
  }, [appointments, appointmentFilter]);

  // Navigation component avec menu hamburger mobile
  const Navigation = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = () => {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleNavClick = (section) => {
      setCurrentSection(section);
      setIsMobileMenuOpen(false); // Fermer le menu mobile après navigation
    };

    return (
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-brand">
            <img 
              src="https://iili.io/KfeqnpI.md.jpg" 
              alt="MBeauty Logo" 
              className="nav-logo"
            />
            <div>
              <h1>MBEAUTY</h1>
              <p>Beauté du regard</p>
            </div>
          </div>

          {/* Menu hamburger button - visible uniquement sur mobile */}
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
            <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
            <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
          </button>

          {/* Navigation links - desktop */}
          <div className="nav-links desktop-nav">
            <button 
              className={currentSection === 'home' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('home')}
            >
              Accueil
            </button>
            <button 
              className={currentSection === 'services' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('services')}
            >
              Services
            </button>
            <button 
              className={currentSection === 'gallery' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('gallery')}
            >
              Galerie
            </button>
            <button 
              className={currentSection === 'booking' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('booking')}
            >
              Réserver
            </button>
            <button 
              className={currentSection === 'reviews' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('reviews')}
            >
              Avis
            </button>
            <button 
              className={currentSection === 'faq' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('faq')}
            >
              FAQ
            </button>
            <button 
              className={currentSection === 'offers' ? 'nav-link active' : 'nav-link'} 
              onClick={() => setCurrentSection('offers')}
            >
              Offres
            </button>
            {!isLoggedIn ? (
              <button 
                className={currentSection === 'client-login' ? 'nav-link active client-link' : 'nav-link client-link'} 
                onClick={() => setCurrentSection('client-login')}
              >
                Mon Compte
              </button>
            ) : (
              <>
                {!currentClient?.is_admin ? (
                  <button 
                    className={currentSection === 'client-area' ? 'nav-link active client-link' : 'nav-link client-link'} 
                    onClick={() => setCurrentSection('client-area')}
                  >
                    {currentClient?.first_name}
                  </button>
                ) : (
                  <button 
                    className={currentSection === 'admin-area' ? 'nav-link active admin-link' : 'nav-link admin-link'} 
                    onClick={() => setCurrentSection('admin-area')}
                  >
                    Admin
                  </button>
                )}
              </>
            )}
          </div>

          {/* Overlay pour fermer le menu */}
          {isMobileMenuOpen && (
            <div 
              className="mobile-menu-overlay"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Navigation dropdown mobile */}
          <div className={`mobile-nav-dropdown ${isMobileMenuOpen ? 'active' : ''}`}>
            <div className="mobile-nav-content">
              <button 
                className={currentSection === 'home' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('home')}
              >
                Accueil
              </button>
              <button 
                className={currentSection === 'services' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('services')}
              >
                Services
              </button>
              <button 
                className={currentSection === 'gallery' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('gallery')}
              >
                Galerie
              </button>
              <button 
                className={currentSection === 'booking' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('booking')}
              >
                Réserver
              </button>
              <button 
                className={currentSection === 'reviews' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('reviews')}
              >
                Avis
              </button>
              <button 
                className={currentSection === 'faq' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('faq')}
              >
                FAQ
              </button>
              <button 
                className={currentSection === 'offers' ? 'mobile-nav-link active' : 'mobile-nav-link'} 
                onClick={() => handleNavClick('offers')}
              >
                Offres
              </button>
              {!isLoggedIn ? (
                <button 
                  className={currentSection === 'client-login' ? 'mobile-nav-link active client-link-mobile' : 'mobile-nav-link client-link-mobile'} 
                  onClick={() => handleNavClick('client-login')}
                >
                  Mon Compte
                </button>
              ) : (
                <>
                  {!currentClient?.is_admin ? (
                    <button 
                      className={currentSection === 'client-area' ? 'mobile-nav-link active client-link-mobile' : 'mobile-nav-link client-link-mobile'} 
                      onClick={() => handleNavClick('client-area')}
                    >
                      {currentClient?.first_name}
                    </button>
                  ) : (
                    <button 
                      className={currentSection === 'admin-area' ? 'mobile-nav-link active admin-link-mobile' : 'mobile-nav-link admin-link-mobile'} 
                      onClick={() => handleNavClick('admin-area')}
                    >
                      Admin
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  };

  // NOUVEAU: Client Area Section améliorée pour mobile
  const ClientAreaSection = () => (
    <div className="container">
      <section className="client-area">
        <div className="client-header">
          <div className="client-info">
            <h2>Bonjour {currentClient?.first_name}</h2>
            <p>Gérez vos rendez-vous et votre profil</p>
          </div>
          <div className="client-actions">
            <button 
              onClick={() => setCurrentSection('booking')}
              className="btn btn-primary btn-compact"
            >
              Nouveau RDV
            </button>
            <PWAInstallButton />
            <button onClick={handleLogout} className="btn btn-outline btn-compact">
              Déconnexion
            </button>
          </div>
        </div>
        
        <div className="client-appointments">
          <h3>Mes rendez-vous</h3>
          {clientAppointments.length === 0 ? (
            <div className="empty-state">
              <p>Aucun rendez-vous trouvé</p>
            </div>
          ) : (
            <div className="appointments-list-mobile">
              {clientAppointments.map((appointment) => (
                <div key={appointment.id} className="appointment-card-mobile">
                  <div className="appointment-header-mobile">
                    <h4>{services[appointment.service_type]?.name || appointment.service_type || "Commande produit uniquement"}</h4>
                    <span className={`status-badge-mobile ${appointment.status}`}>
                      {appointment.status === 'pending' ? 'En cours' : 
                       appointment.status === 'confirmed' ? 'Confirmé' : 
                       appointment.status === 'cancelled' ? 'Annulé' : 'Terminé'}
                    </span>
                  </div>
                  <div className="appointment-details-mobile">
                    {appointment.appointment_date && appointment.appointment_date !== '1970-01-01' && (
                      <div className="detail-row">
                        <span className="label">Date:</span>
                        <span className="value">{new Date(appointment.appointment_date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                    {appointment.appointment_time && appointment.appointment_time !== '00:00' && (
                      <div className="detail-row">
                        <span className="label">Heure:</span>
                        <span className="value">{appointment.appointment_time}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="label">Prix:</span>
                      <span className="value">{appointment.price}€</span>
                    </div>
                    {appointment.add_serum_mbeautycare && (
                      <div className="detail-row">
                        <span className="label">Produit:</span>
                        <span className="value">Sérum MbeautyCare</span>
                      </div>
                    )}
                    {appointment.delivery_address && (
                      <div className="detail-row">
                        <span className="label">Livraison:</span>
                        <span className="value">{appointment.delivery_address}</span>
                      </div>
                    )}
                    {appointment.additional_notes && (
                      <div className="detail-row">
                        <span className="label">Notes:</span>
                        <span className="value">{appointment.additional_notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="client-orders">
          <h3>Mes commandes</h3>
          {clientOrders.length === 0 ? (
            <div className="empty-state">
              <p>Aucune commande trouvée</p>
            </div>
          ) : (
            <div className="appointments-list-mobile">
              {clientOrders.map((order) => (
                <div key={order.id} className="appointment-card-mobile">
                  <div className="appointment-header-mobile">
                    <h4>{order.product_name}</h4>
                    <span className={`status-badge-mobile ${order.status}`}>{order.status}</span>
                  </div>
                  <div className="appointment-details-mobile">
                    <div className="detail-row">
                      <span className="label">Prix:</span>
                      <span className="value">{order.product_price}€</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Quantité:</span>
                      <span className="value">{order.quantity}</span>
                    </div>
                    {order.notes && (
                      <div className="detail-row">
                        <span className="label">Notes:</span>
                        <span className="value">{order.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );

  // Fonctions de navigation de semaine
  const goToPreviousWeek = () => {
    setSelectedWeekMonday(prev => {
      const newMonday = new Date(prev);
      newMonday.setDate(prev.getDate() - 7);
      return newMonday;
    });
  };

  const goToNextWeek = () => {
    setSelectedWeekMonday(prev => {
      const newMonday = new Date(prev);
      newMonday.setDate(prev.getDate() + 7);
      return newMonday;
    });
  };

  // Fonctions de navigation de mois
  const goToPreviousMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() - 1);
      return newMonth;
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + 1);
      return newMonth;
    });
  };

  // Fonction pour générer l'image du planning mensuel (format Instagram Story)
  const generateMonthlyPlanningImage = () => {
    console.log('=== GÉNÉRATION PLANNING MENSUEL ===');
    console.log('Tous les créneaux disponibles:', availableSlots.length);
    
    // Utiliser le mois sélectionné
    const firstDay = new Date(selectedMonth);
    const lastDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
    lastDay.setHours(23, 59, 59, 999);
    
    console.log('Période:', firstDay.toLocaleDateString('fr-FR'), 'à', lastDay.toLocaleDateString('fr-FR'));
    
    // Filtrer les créneaux du mois - UNIQUEMENT LES DISPONIBLES
    const monthSlots = availableSlots.filter(slot => {
      if (slot.is_available !== true) {
        return false;
      }
      
      let slotDate;
      if (typeof slot.date === 'string') {
        const parts = slot.date.split('-');
        if (parts.length === 3) {
          slotDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          slotDate = new Date(slot.date);
        }
      } else {
        slotDate = new Date(slot.date);
      }
      
      const slotDateOnly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
      const firstDayOnly = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
      const lastDayOnly = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());
      
      return slotDateOnly >= firstDayOnly && slotDateOnly <= lastDayOnly;
    });
    
    console.log('Créneaux disponibles du mois:', monthSlots.length);
    
    // Organiser les créneaux par jour
    const slotsByDate = {};
    monthSlots.forEach(slot => {
      let dateKey;
      if (typeof slot.date === 'string' && slot.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateKey = slot.date;
      } else {
        const d = new Date(slot.date);
        dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      
      if (!slotsByDate[dateKey]) {
        slotsByDate[dateKey] = [];
      }
      slotsByDate[dateKey].push(slot.time);
    });
    
    // Trier les créneaux de chaque jour
    Object.keys(slotsByDate).forEach(date => {
      slotsByDate[date].sort();
    });
    
    // CALCUL DYNAMIQUE DE LA HAUTEUR - basé sur les jours avec créneaux uniquement
    const headerHeight = 400;
    const footerHeight = 200;
    const daysInMonth = lastDay.getDate();
    
    // Compter le nombre réel de jours avec des créneaux
    let daysWithSlots = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const slots = slotsByDate[dateKey] || [];
      if (slots.length > 0) {
        daysWithSlots++;
      }
    }
    
    // Calculer la hauteur du contenu basée sur les jours réels
    const dayHeight = 70; // Hauteur par jour avec créneaux
    const contentHeight = (daysWithSlots * dayHeight) + 100; // +100 pour l'espacement
    const totalHeight = Math.max(1920, headerHeight + contentHeight + footerHeight);
    
    console.log(`Jours avec créneaux: ${daysWithSlots}, Hauteur calculée: ${totalHeight}px`);
    
    // Créer le canvas avec un canvas temporaire pour le rendu
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const width = 1080;
    canvas.width = width;
    canvas.height = totalHeight;
    
    // Fond blanc
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, totalHeight);
    
    const margin = 60;
    let currentY = 80;
    
    // En-tête
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('MBEAUTY', width / 2, currentY);
    
    currentY += 50;
    ctx.font = '24px Arial';
    ctx.fillText('Beauté du regard', width / 2, currentY);
    
    currentY += 80;
    ctx.font = 'bold 36px Arial';
    ctx.fillText('PLANNING', width / 2, currentY);
    
    currentY += 50;
    ctx.font = '22px Arial';
    const monthName = firstDay.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    ctx.fillText(monthName.charAt(0).toUpperCase() + monthName.slice(1), width / 2, currentY);
    
    // Ligne de séparation
    currentY += 60;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(margin, currentY);
    ctx.lineTo(width - margin, currentY);
    ctx.stroke();
    
    currentY += 60;
    
    // Afficher uniquement les jours avec des créneaux disponibles
    const daysOfWeek = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const slots = slotsByDate[dateKey] || [];
      const dayName = daysOfWeek[currentDate.getDay()];
      
      // Afficher uniquement les jours avec des créneaux disponibles
      if (slots.length > 0) {
        // Date et jour
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 26px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`${dayName} ${String(day).padStart(2, '0')}`, margin, currentY);
        
        // Créneaux sur la même ligne
        ctx.font = '22px Arial';
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'right';
        const slotsText = slots.slice(0, 5).join('  •  ') + (slots.length > 5 ? '...' : '');
        ctx.fillText(slotsText, width - margin, currentY);
        
        currentY += 45;
        
        // Ligne de séparation
        ctx.strokeStyle = '#EEEEEE';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(margin, currentY);
        ctx.lineTo(width - margin, currentY);
        ctx.stroke();
        
        currentY += 25;
      }
    }
    
    // Pied de page
    currentY += 40;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(margin, currentY);
    ctx.lineTo(width - margin, currentY);
    ctx.stroke();
    
    currentY += 50;
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Réservez votre rendez-vous', width / 2, currentY);
    
    
    currentY += 60; // Marge finale
    
    // Recadrer le canvas à la hauteur réelle du contenu
    const finalHeight = Math.min(currentY, totalHeight);
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    finalCanvas.width = width;
    finalCanvas.height = finalHeight;
    
    // Copier le contenu dans le canvas final
    finalCtx.drawImage(canvas, 0, 0, width, finalHeight, 0, 0, width, finalHeight);
    
    console.log(`Hauteur finale du canvas: ${finalHeight}px`);
    
    // Télécharger l'image
    const monthStr = firstDay.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    finalCanvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `planning-${monthStr.replace(/ /g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`Planning mensuel téléchargé avec succès !\n${monthSlots.length} créneaux affichés`);
    }, 'image/png');
  };

  // Fonction pour générer l'image du planning hebdomadaire (format Instagram Story)
  const generateWeeklyPlanningImage = () => {
    console.log('=== GÉNÉRATION PLANNING ===');
    console.log('Tous les créneaux disponibles:', availableSlots.length);
    
    // Utiliser la semaine sélectionnée
    const monday = new Date(selectedWeekMonday);
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    console.log('Période:', monday.toLocaleDateString('fr-FR'), 'à', sunday.toLocaleDateString('fr-FR'));
    
    // Filtrer les créneaux de la semaine courante - UNIQUEMENT LES DISPONIBLES
    const weekSlots = availableSlots.filter(slot => {
      // IMPORTANT: Ne garder QUE les créneaux disponibles
      if (slot.is_available !== true) {
        return false;
      }
      
      // Parser la date en timezone locale (France) pour éviter le décalage UTC
      let slotDate;
      if (typeof slot.date === 'string') {
        // Si format "2025-10-27", extraire les composants et créer une date locale
        const parts = slot.date.split('-');
        if (parts.length === 3) {
          slotDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          slotDate = new Date(slot.date);
        }
      } else {
        slotDate = new Date(slot.date);
      }
      
      // Comparer uniquement les dates (sans les heures)
      const slotDateOnly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
      const mondayOnly = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
      const sundayOnly = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate());
      
      return slotDateOnly >= mondayOnly && slotDateOnly <= sundayOnly;
    });
    
    // Debug: Afficher tous les créneaux trouvés
    console.log('=== CRÉNEAUX DISPONIBLES DE LA SEMAINE ===');
    console.log('Créneaux disponibles trouvés:', weekSlots.length);
    weekSlots.forEach(s => {
      console.log(`  - ${s.date} à ${s.time}`);
    });
    
    // Organiser les créneaux par jour
    const daysOfWeek = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const slotsByDay = {};
    
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      const dayKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
      slotsByDay[dayKey] = [];
    }
    
    weekSlots.forEach(slot => {
      // Parser la date du slot en local pour correspondre aux clés
      let slotDateKey;
      if (typeof slot.date === 'string') {
        // Si format "2025-10-27", utiliser directement
        if (slot.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          slotDateKey = slot.date;
        } else {
          const parts = slot.date.split('-');
          if (parts.length === 3) {
            slotDateKey = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
          }
        }
      } else {
        const d = new Date(slot.date);
        slotDateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      
      if (slotsByDay[slotDateKey]) {
        slotsByDay[slotDateKey].push(slot.time);
      }
    });
    
    console.log('Créneaux organisés par jour:', slotsByDay);
    
    // Calculer la hauteur nécessaire en fonction du nombre de créneaux
    const headerHeight = 400;
    const footerHeight = 200;
    let contentHeight = 0;
    
    // Calculer l'espace nécessaire pour chaque jour
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      // FIX TIMEZONE: Utiliser les composants locaux au lieu de toISOString() qui convertit en UTC
      const dayKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
      const slots = slotsByDay[dayKey] || [];
      
      // Hauteur pour le nom du jour + ligne
      contentHeight += 80;
      
      if (slots.length > 0) {
        // Calculer le nombre de lignes nécessaires (3 créneaux par ligne)
        const numLines = Math.ceil(slots.length / 3);
        contentHeight += (numLines * 40) + 40; // 40px par ligne + espacement
      } else {
        contentHeight += 50; // Message "Aucun créneau"
      }
      
      contentHeight += 30; // Espacement entre jours
    }
    
    // Hauteur totale dynamique
    const totalHeight = Math.max(1920, headerHeight + contentHeight + footerHeight);
    
    // Créer le canvas - Format Instagram Story (9:16)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Dimensions Instagram Story
    const width = 1080;
    const height = totalHeight;
    
    canvas.width = width;
    canvas.height = height;
    
    // Fond blanc
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    
    // Marges
    const margin = 60;
    const contentWidth = width - (margin * 2);
    
    // En-tête avec logo et titre
    let currentY = 80;
    
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('MBEAUTY', width / 2, currentY);
    
    currentY += 50;
    ctx.font = '24px Arial';
    ctx.fillText('Beauté du regard', width / 2, currentY);
    
    currentY += 80;
    ctx.font = 'bold 36px Arial';
    ctx.fillText('PLANNING', width / 2, currentY);
    
    currentY += 50;
    ctx.font = '22px Arial';
    const weekStart = monday.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    const weekEnd = sunday.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    ctx.fillText(`Du ${weekStart} au ${weekEnd}`, width / 2, currentY);
    
    // Ligne de séparation
    currentY += 60;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(margin, currentY);
    ctx.lineTo(width - margin, currentY);
    ctx.stroke();
    
    currentY += 60;
    
    // Afficher les créneaux jour par jour (format vertical)
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      // FIX TIMEZONE: Utiliser les composants locaux au lieu de toISOString() qui convertit en UTC
      const dayKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
      const slots = slotsByDay[dayKey] || [];
      
      // Trier les créneaux par heure
      slots.sort();
      
      // Nom du jour et date
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'left';
      const dateStr = dayDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      ctx.fillText(`${daysOfWeek[i]} ${dateStr}`, margin, currentY);
      
      currentY += 15;
      
      // Ligne sous le jour
      ctx.strokeStyle = '#CCCCCC';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(margin, currentY);
      ctx.lineTo(width - margin, currentY);
      ctx.stroke();
      
      currentY += 35;
      
      // Créneaux horaires
      if (slots.length > 0) {
        ctx.font = '26px Arial';
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'left';
        
        // Afficher TOUS les créneaux sur plusieurs lignes
        let line = '';
        
        slots.forEach((time, index) => {
          if (line) {
            line += '  •  ' + time;
          } else {
            line = time;
          }
          
          // Nouvelle ligne tous les 3 créneaux ou à la fin
          if ((index + 1) % 3 === 0 || index === slots.length - 1) {
            ctx.fillText(line, margin + 20, currentY);
            currentY += 40;
            line = '';
          }
        });
        
        currentY += 10;
      } else {
        ctx.font = 'italic 24px Arial';
        ctx.fillStyle = '#999999';
        ctx.textAlign = 'left';
        ctx.fillText('Aucun créneau disponible', margin + 20, currentY);
        currentY += 50;
      }
      
      currentY += 30;
    }
    
    // Pied de page
    currentY += 40;
    
    // Ligne de séparation
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(margin, currentY);
    ctx.lineTo(width - margin, currentY);
    ctx.stroke();
    
    currentY += 50;
    
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Réservez votre rendez-vous', width / 2, currentY);
    
    
    currentY += 60; // Marge finale
    
    // Recadrer le canvas à la hauteur réelle du contenu
    const finalHeight = Math.min(currentY, height);
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    finalCanvas.width = width;
    finalCanvas.height = finalHeight;
    
    // Copier le contenu dans le canvas final
    finalCtx.drawImage(canvas, 0, 0, width, finalHeight, 0, 0, width, finalHeight);
    
    console.log(`Hauteur finale du canvas hebdomadaire: ${finalHeight}px`);
    
    // Télécharger l'image
    finalCanvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `planning-story-${weekStart.replace(/\//g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`Planning téléchargé avec succès !\n${weekSlots.length} créneaux affichés`);
    }, 'image/png');
  };

  // NOUVEAU: Admin Area Section avec système d'onglets
  const AdminAreaSection = () => {
    // Calculer l'affichage selon le mode
    let periodDisplay = '';
    let goToPrevious, goToNext;
    
    if (planningMode === 'week') {
      const selectedSunday = new Date(selectedWeekMonday);
      selectedSunday.setDate(selectedWeekMonday.getDate() + 6);
      periodDisplay = `${selectedWeekMonday.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} - ${selectedSunday.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
      goToPrevious = goToPreviousWeek;
      goToNext = goToNextWeek;
    } else {
      periodDisplay = selectedMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      periodDisplay = periodDisplay.charAt(0).toUpperCase() + periodDisplay.slice(1);
      goToPrevious = goToPreviousMonth;
      goToNext = goToNextMonth;
    }

    return (
      <div className="container">
        <section className="admin">
          <div className="admin-header-new">
            <div className="admin-info">
              <h2>Administration MBeauty</h2>
              <p>Gestion de votre activité</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Sélecteur de mode */}
              <div style={{ display: 'flex', gap: '5px', backgroundColor: '#f0f0f0', borderRadius: '6px', padding: '4px' }}>
                <button 
                  onClick={() => setPlanningMode('week')} 
                  className={planningMode === 'week' ? 'btn btn-primary btn-compact' : 'btn btn-outline btn-compact'}
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Semaine
                </button>
                <button 
                  onClick={() => setPlanningMode('month')} 
                  className={planningMode === 'month' ? 'btn btn-primary btn-compact' : 'btn btn-outline btn-compact'}
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Mois
                </button>
              </div>
              
              {/* Navigation de période */}
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <button 
                  onClick={goToPrevious} 
                  className="btn btn-outline btn-compact"
                  title={planningMode === 'week' ? 'Semaine précédente' : 'Mois précédent'}
                  style={{ padding: '8px 12px' }}
                >
                  ←
                </button>
                <span style={{ fontSize: '14px', whiteSpace: 'nowrap', minWidth: '140px', textAlign: 'center' }}>
                  {periodDisplay}
                </span>
                <button 
                  onClick={goToNext} 
                  className="btn btn-outline btn-compact"
                  title={planningMode === 'week' ? 'Semaine suivante' : 'Mois suivant'}
                  style={{ padding: '8px 12px' }}
                >
                  →
                </button>
              </div>
              
              {/* Bouton de téléchargement */}
              <button 
                onClick={planningMode === 'week' ? generateWeeklyPlanningImage : generateMonthlyPlanningImage} 
                className="btn btn-primary btn-compact"
                title={`Télécharger le planning ${planningMode === 'week' ? 'de la semaine' : 'du mois'}`}
              >
                📅 Planning
              </button>
              <button onClick={handleLogout} className="btn btn-outline btn-compact">
                Déconnexion
              </button>
            </div>
          </div>

        {/* Layout admin : sidebar + contenu */}
        <div className="admin-layout">
        {/* Onglets de navigation */}
        <div className="admin-tabs" data-testid="admin-sidebar">
          <span className="admin-sidebar-title">Gestion</span>
          <button 
            className={adminActiveTab === 'dashboard' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setAdminActiveTab('dashboard')}
            data-testid="admin-tab-dashboard"
          >
            <span className="admin-tab-ico">📊</span>
            <span className="admin-tab-txt">Tableau</span>
          </button>
          <button 
            className={adminActiveTab === 'appointments' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setAdminActiveTab('appointments')}
            data-testid="admin-tab-appointments"
          >
            <span className="admin-tab-ico">📅</span>
            <span className="admin-tab-txt">Rendez-vous</span>
          </button>
          <button 
            className={adminActiveTab === 'reviews' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setAdminActiveTab('reviews')}
            data-testid="admin-tab-reviews"
          >
            <span className="admin-tab-ico">⭐</span>
            <span className="admin-tab-txt">Avis</span>
            {pendingReviews.length > 0 && (
              <span className="tab-badge">{pendingReviews.length}</span>
            )}
          </button>
          <button 
            className={adminActiveTab === 'slots' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setAdminActiveTab('slots')}
            data-testid="admin-tab-slots"
          >
            <span className="admin-tab-ico">⏰</span>
            <span className="admin-tab-txt">Créneaux</span>
          </button>
        </div>

        {/* Contenu des onglets */}
        <div className="admin-content">
          {/* Tableau de bord */}
          {adminActiveTab === 'dashboard' && (
            <div className="admin-dashboard">
              <div className="admin-stats-mobile">
                <div className="stat-card-mobile">
                  <div className="stat-number">{adminStats.total}</div>
                  <div className="stat-label">Total RDV</div>
                </div>
                <div className="stat-card-mobile pending">
                  <div className="stat-number">{adminStats.pending}</div>
                  <div className="stat-label">En cours</div>
                </div>
                <div className="stat-card-mobile confirmed">
                  <div className="stat-number">{adminStats.confirmed}</div>
                  <div className="stat-label">Confirmés</div>
                </div>
                <div className="stat-card-mobile reviews">
                  <div className="stat-number">{adminStats.pendingReviews}</div>
                  <div className="stat-label">Avis à valider</div>
                </div>
              </div>

              <div className="admin-quick-actions">
                <h3>Actions rapides</h3>
                <div className="quick-actions-grid">
                  <button 
                    onClick={() => setAdminActiveTab('appointments')}
                    className="quick-action-btn"
                  >
                    <span className="icon">📅</span>
                    <span>Gérer les RDV</span>
                    {adminStats.pending > 0 && <span className="badge">{adminStats.pending}</span>}
                  </button>
                  <button 
                    onClick={() => setAdminActiveTab('reviews')}
                    className="quick-action-btn"
                  >
                    <span className="icon">⭐</span>
                    <span>Valider les avis</span>
                    {adminStats.pendingReviews > 0 && <span className="badge">{adminStats.pendingReviews}</span>}
                  </button>
                  <button 
                    onClick={() => setAdminActiveTab('slots')}
                    className="quick-action-btn"
                  >
                    <span className="icon">⏰</span>
                    <span>Ajouter créneaux</span>
                  </button>
                  <button 
                    onClick={refreshAdminData}
                    className="quick-action-btn"
                  >
                    <span className="icon">🔄</span>
                    <span>Actualiser</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Gestion des rendez-vous */}
          {adminActiveTab === 'appointments' && (
            <div className="admin-appointments">
              <div className="appointments-header">
                <h3>Gestion des rendez-vous ({filteredAppointments.length})</h3>
                <div className="appointments-actions">
                  <button 
                    onClick={deleteCompletedAppointments}
                    className="btn btn-secondary btn-sm"
                    title="Supprimer tous les créneaux et rendez-vous passés depuis plus de 24H, ainsi que les rendez-vous annulés"
                    data-testid="admin-cleanup-past-cancelled-btn"
                  >
                    🗑️ Nettoyer (passés +24H et annulés)
                  </button>
                  <button 
                    onClick={() => {
                      console.log('Debug - Tous les rendez-vous:', appointments.map(apt => ({
                        id: apt.id,
                        date: apt.appointment_date,
                        status: apt.status,
                        service: apt.service_type,
                        client: apt.client_name
                      })));
                    }}
                    className="btn btn-outline btn-sm"
                  >
                    🔍 Debug
                  </button>
                  <button 
                    onClick={refreshAdminData}
                    className="btn btn-outline btn-sm"
                  >
                    🔄 Actualiser
                  </button>
                </div>
              </div>

              {/* Filtres de statut */}
              <div className="appointment-filters">
                <button 
                  className={appointmentFilter === 'all' ? 'filter-btn active' : 'filter-btn'}
                  onClick={() => setAppointmentFilter('all')}
                >
                  Tous ({appointments.length})
                </button>
                <button 
                  className={appointmentFilter === 'pending' ? 'filter-btn active' : 'filter-btn'}
                  onClick={() => setAppointmentFilter('pending')}
                >
                  En cours ({appointments.filter(a => a.status === 'pending').length})
                </button>
                <button 
                  className={appointmentFilter === 'confirmed' ? 'filter-btn active' : 'filter-btn'}
                  onClick={() => setAppointmentFilter('confirmed')}
                >
                  Confirmés ({appointments.filter(a => a.status === 'confirmed').length})
                </button>
                <button 
                  className={appointmentFilter === 'cancelled' ? 'filter-btn active' : 'filter-btn'}
                  onClick={() => setAppointmentFilter('cancelled')}
                >
                  Annulés ({appointments.filter(a => a.status === 'cancelled').length})
                </button>
              </div>

              {loadingAppointments ? (
                <div className="loading-state">Chargement des rendez-vous...</div>
              ) : (
                <div className="appointments-list-admin">
                  {filteredAppointments.map((appointment) => (
                    <div key={appointment.id} className={`appointment-item-admin ${appointment.status}`}>
                      <div className="appointment-header-admin">
                        <h4>{appointment.client_name}</h4>
                        <span className={`status-badge-admin ${appointment.status}`}>
                          {appointment.status === 'pending' ? 'En cours' : 
                           appointment.status === 'confirmed' ? 'Confirmé' : 
                           appointment.status === 'cancelled' ? 'Annulé' : 'Terminé'}
                        </span>
                      </div>
                      
                      <div className="appointment-details-admin">
                        <div className="detail-grid">
                          <div className="detail-item">
                            <span className="label">Email:</span>
                            <span className="value">{appointment.client_email}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Téléphone:</span>
                            <span className="value">{appointment.client_phone}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Service:</span>
                            <span className="value">
                              {appointment.service_type ? 
                                (services[appointment.service_type]?.name || appointment.service_type) :
                                "Commande produit uniquement"
                              }
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Prix:</span>
                            <span className="value">{appointment.price}€</span>
                          </div>
                          
                          {appointment.service_type && (
                            <>
                              <div className="detail-item">
                                <span className="label">Date:</span>
                                <span className="value">{new Date(appointment.appointment_date).toLocaleDateString('fr-FR')}</span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Heure:</span>
                                <span className="value">{appointment.appointment_time}</span>
                              </div>
                            </>
                          )}
                          
                          {appointment.add_lash_botox && (
                            <div className="detail-item">
                              <span className="label">Option:</span>
                              <span className="value">Lash Botox (+5€)</span>
                            </div>
                          )}
                          
                          {appointment.add_serum_mbeautycare && (
                            <div className="detail-item">
                              <span className="label">Produit:</span>
                              <span className="value">Sérum MbeautyCare (+15€)</span>
                            </div>
                          )}
                          
                          {appointment.delivery_address && (
                            <div className="detail-item full-width">
                              <span className="label">Livraison:</span>
                              <span className="value">{appointment.delivery_address}</span>
                            </div>
                          )}
                          
                          {appointment.additional_notes && (
                            <div className="detail-item full-width">
                              <span className="label">Notes:</span>
                              <span className="value">{appointment.additional_notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {appointment.status === 'pending' && (
                        <div className="appointment-actions-admin">
                          <button 
                            onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                            className="btn btn-primary btn-sm"
                            disabled={updatingAppointment === appointment.id}
                          >
                            {updatingAppointment === appointment.id ? 'Confirmation...' : '✅ Confirmer'}
                          </button>
                          <button 
                            onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                            className="btn btn-secondary btn-sm"
                            disabled={updatingAppointment === appointment.id}
                          >
                            ❌ Annuler
                          </button>
                        </div>
                      )}
                      
                      {appointment.status === 'confirmed' && (
                        <div className="appointment-actions-admin">
                          <button 
                            onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                            className="btn btn-secondary btn-sm"
                            disabled={updatingAppointment === appointment.id}
                          >
                            {updatingAppointment === appointment.id ? 'Annulation...' : '❌ Annuler'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Gestion des avis */}
          {adminActiveTab === 'reviews' && (
            <div className="admin-reviews">
              <div className="reviews-header">
                <h3>Avis en attente de validation ({pendingReviews.length})</h3>
                {pendingReviews.length > 0 && (
                  <button 
                    onClick={refreshAdminData}
                    className="btn btn-outline btn-sm"
                  >
                    🔄 Actualiser
                  </button>
                )}
              </div>
              
              {pendingReviews.length === 0 ? (
                <div className="empty-state">
                  <p>Aucun avis en attente de validation</p>
                </div>
              ) : (
                <div className="pending-reviews-list">
                  {pendingReviews.map((review) => (
                    <div key={review.id} className="pending-review-item-mobile">
                      <div className="review-header-mobile">
                        <div className="review-rating-mobile">
                          {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                        </div>
                        <span className="review-date-mobile">
                          {new Date(review.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      
                      <div className="review-content-mobile">
                        <p className="review-comment-mobile">"{review.comment}"</p>
                        <div className="review-author-mobile">
                          <strong>{review.client_name}</strong>
                          {review.service_type && (
                            <span className="review-service-mobile">
                              {services[review.service_type]?.name || review.service_type}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="review-actions-mobile">
                        <button 
                          onClick={() => approveReview(review.id)}
                          className="btn btn-primary btn-sm"
                        >
                          ✅ Approuver
                        </button>
                        <button 
                          onClick={() => rejectReview(review.id)}
                          className="btn btn-secondary btn-sm"
                        >
                          ❌ Rejeter
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Gestion des créneaux */}
          {adminActiveTab === 'slots' && (
            <div className="admin-slots">
              <div className="slots-header">
                <h3>Gestion des créneaux</h3>
              </div>
              
              <div className="slot-form-mobile">
                <h4>Ajouter un nouveau créneau</h4>
                <form onSubmit={addTimeSlot} className="form slot-add-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Date</label>
                      <input
                        type="date"
                        name="date"
                        value={newSlot.date}
                        onChange={handleNewSlotChange}
                        required
                        className="form-input"
                        data-testid="slot-date-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Heure</label>
                      <input
                        type="time"
                        name="time"
                        value={newSlot.time}
                        onChange={handleNewSlotChange}
                        required
                        className="form-input"
                        data-testid="slot-time-input"
                      />
                    </div>
                  </div>

                  <div className="quick-times">
                    <span className="quick-times-label">Horaires rapides</span>
                    <div className="quick-times-chips">
                      {['09:00','10:00','11:00','12:00','14:00','15:00','16:00','17:00','18:00','19:00'].map(t => (
                        <button
                          type="button"
                          key={t}
                          className={`time-chip ${newSlot.time === t ? 'active' : ''}`}
                          onClick={() => setNewSlot(prev => ({ ...prev, time: t }))}
                          data-testid={`quick-time-${t}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg" style={{width: '100%'}} data-testid="add-slot-btn">
                    + Ajouter le créneau
                  </button>
                </form>
              </div>

              <div className="slots-list">
                <h4>Créneaux existants</h4>
                <div className="slots-grid-mobile">
                  {availableSlots.map(slot => (
                    <div key={slot.id} className={`slot-item-mobile ${slot.is_available ? 'available' : 'booked'}`}>
                      <div className="slot-info-mobile">
                        <div className="slot-datetime-mobile">
                          {new Date(slot.date).toLocaleDateString('fr-FR')} à {slot.time}
                        </div>
                        <span className={`slot-status-mobile ${slot.is_available ? 'available' : 'booked'}`}>
                          {slot.is_available ? '✅ Disponible' : '❌ Réservé'}
                        </span>
                      </div>
                      <button 
                        onClick={() => deleteTimeSlot(slot.id)}
                        className="btn btn-secondary btn-sm"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </section>
    </div>
    );
  };

  // Home section

  // Nouvelle section Offres améliorée avec FAQ détaillée
  const OffersSection = () => {
    const product = {
      id: 'serum-15',
      name: 'MbeautyCare',
      price: 15,
      description: "Sérum cils & sourcils – 12 ml",
      image: "https://customer-assets.emergentagent.com/job_mbeauty-addon/artifacts/2mvnt270_image.png"
    };

    const [showFAQ, setShowFAQ] = useState(false);

    const faqData = [
      {
        question: "D'où provient le soin MbeautyCare ?",
        answer: "Le mascara est made in France 🇫🇷 il a été fabriqué dans un laboratoire, testé dermatologiquement et spécialement conçu pour les cils et sourcils."
      },
      {
        question: "De quoi le soin est-il composé ?",
        answer: "Le soin est composé à 100% d'huile naturelle, végan et bio."
      },
      {
        question: "Quelles sont les bienfaits du MbeautyCare ?",
        answer: "Il favorise la pousse et la densité de vos cils et sourcils, il lutte contre la chute et les nourrit en profondeur."
      },
      {
        question: "Comment se conserve-t-il ?",
        answer: "Placez-le dans un endroit sec, à l'abri de la chaleur, et surtout bien le refermer après chaque utilisation."
      },
      {
        question: "Au bout de combien de temps voit-on les résultats ?",
        answer: "Appliquer le soin quotidiennement tous les soirs sur une peau sèche et propre, les premiers résultats seront visibles au bout de six à huit semaines."
      }
    ];

    return (
      <div className="container">
        <section className="offers" id="offres">
          <div className="section-header">
            <h2>Offres</h2>
            <p>Découvrez nos produits exclusifs pour entretenir votre regard au quotidien.</p>
          </div>
          
          <div className="offers-enhanced-layout">
            <div className="product-showcase">
              <div className="product-main-card">
                <div className="product-image-section">
                  <img src={product.image} alt={product.name} />
                  <div className="product-badge">Made in France 🇫🇷</div>
                </div>
                
                <div className="product-info-section">
                  <div className="product-header">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-subtitle">CILS & SOURCILS</p>
                    <p className="product-description">{product.description}</p>
                  </div>

                  <div className="product-benefits">
                    <h4>Bienfaits du MbeautyCare</h4>
                    <ul>
                      <li>✨ Favorise la pousse et la densité</li>
                      <li>🛡️ Lutte contre la chute</li>
                      <li>💧 Nourrit en profondeur</li>
                      <li>🌿 100% huile naturelle, végan et bio</li>
                      <li>🧪 Testé dermatologiquement</li>
                    </ul>
                  </div>

                  <div className="product-usage">
                    <h4>Mode d'emploi</h4>
                    <p>Appliquer quotidiennement tous les soirs sur une peau sèche et propre. 
                    Les premiers résultats seront visibles au bout de 6 à 8 semaines.</p>
                  </div>

                  <div className="product-purchase">
                    <div className="price-section">
                      <span className="current-price">{product.price}€</span>
                      <span className="price-note">Livraison incluse</span>
                    </div>
                    <div className="purchase-actions">
                      <button 
                        className="btn btn-primary btn-lg" 
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            add_serum_mbeautycare: true
                          }));
                          setCurrentSection('booking');
                        }}
                      >
                        Ajouter à une réservation
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section FAQ détaillée */}
              <div className="product-faq-section">
                <div className="faq-header">
                  <h3>FAQ sur le soin MbeautyCare</h3>
                  <button 
                    className="faq-toggle-btn"
                    onClick={() => setShowFAQ(!showFAQ)}
                  >
                    {showFAQ ? 'Masquer les détails' : 'Voir tous les détails'}
                  </button>
                </div>
                
                {showFAQ && (
                  <div className="faq-details">
                    {faqData.map((item, index) => (
                      <div key={index} className="faq-item-detailed">
                        <h4>{item.question}</h4>
                        <p>{item.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const HomeSection = () => (
    <div className="container">
      <section className="hero">
        <div className="hero-content">
          <h1>MBEAUTY</h1>
          <p className="hero-subtitle">Beauté du regard</p>
          <p className="hero-description">
            Spécialiste en rehaussement de cils, browlift et restructuration de sourcils. 
            Révélez la beauté naturelle de votre regard avec nos soins professionnels.
          </p>
          <div className="hero-actions">
            <button 
              onClick={() => setCurrentSection('booking')}
              className="btn btn-primary btn-lg"
            >
              Réserver un soin
            </button>
            <button 
              onClick={() => setCurrentSection('offers')}
              className="btn btn-outline btn-lg"
            >
              Voir nos offres
            </button>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-photo-wrap">
            <span className="hero-frame hero-frame-tl" aria-hidden="true"></span>
            <span className="hero-frame hero-frame-br" aria-hidden="true"></span>
            <span className="hero-blob" aria-hidden="true"></span>
            <span className="hero-dots" aria-hidden="true"></span>
            <img 
              src="https://iili.io/KfeKlcl.md.jpg" 
              alt="MBeauty - Beauté du regard"
              className="hero-main-image"
            />
          </div>
        </div>
      </section>

      <section className="features">
        <div className="section-header">
          <h2>Pourquoi choisir MBeauty</h2>
          <p>Excellence et professionnalisme dans la beauté du regard. Découvrez ce qui fait de nous votre partenaire beauté de confiance.</p>
        </div>

        <div className="feature-grid-new">
          <div className="feature-item-new">
            <div className="feature-icon-new">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
            </div>
            <h3>Expertise professionnelle</h3>
            <p>Techniques avancées et produits haut de gamme pour des résultats exceptionnels et durables.</p>
          </div>

          <div className="feature-item-new">
            <div className="feature-icon-new">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>
            <h3>Approche personnalisée</h3>
            <p>Chaque soin est adapté à la forme de vos yeux et à vos préférences pour un résultat unique.</p>
          </div>

          <div className="feature-item-new">
            <div className="feature-icon-new">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3>Rendez-vous flexibles</h3>
            <p>Réservation en ligne simple et créneaux adaptés à votre emploi du temps.</p>
          </div>
        </div>
      </section>
    </div>
  );

  // SECTION SERVICES - Design exact selon l'image fournie (définition déjà ajoutée plus haut)
  // Placeholder sections supprimés pour éviter les conflits

  // Ancienne définition ServicesSection supprimée pour éviter conflit
  const _ServicesSection = () => {
    // Fonction pour rediriger vers la réservation avec le service pré-sélectionné
    const handleServiceBooking = (serviceType) => {
      // Mise à jour du formulaire avec le service sélectionné
      setFormData(prev => ({
        ...prev,
        service_type: serviceType
      }));
      // Redirection vers la section booking
      setCurrentSection('booking');
    };

    return (
      <div className="container">
        <section className="services">
          <div className="section-header">
            <h2>Nos Services</h2>
            <p>Des soins professionnels pour sublimer votre regard avec nos techniques expertes</p>
          </div>

          {loadingServices ? (
            <div className="loading">Chargement des services...</div>
          ) : (
            <div className="services-new-layout">
              {/* Rehaussement de Cils */}
              <div className="service-category-new">
                <h3 className="category-title-new">Rehaussement de Cils</h3>
                <div className="service-row-new">
                  <div className="service-card-new">
                    <h4>Rehaussement Simple</h4>
                    <div className="price-new">30€</div>
                    <p className="service-description-new">
                      Courbure naturelle et ouverture du regard pour des cils parfaitement rehaussés.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Effet longue durée</p>
                      <p>• Technique professionnelle</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('lash_lift')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new popular-card-new">
                    <div className="badge-popular-new">POPULAIRE</div>
                    <h4>Rehaussement + Teinture</h4>
                    <div className="price-new">35€</div>
                    <p className="service-description-new">
                      Courbure parfaite avec intensification de la couleur pour un regard plus intense.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Coloration incluse</p>
                      <p>• Résultat spectaculaire</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('lash_lift_tint')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new premium-card-new">
                    <h4>Formule Complète + Lash Botox</h4>
                    <div className="price-new">40€</div>
                    <p className="service-description-new">
                      Soin complet avec rehaussement, teinture et traitement nourrissant lash botox.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Lash botox inclus</p>
                      <p>• Soin premium</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('lash_lift_tint_botox')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Browlift */}
              <div className="service-category-new">
                <h3 className="category-title-new">Browlift</h3>
                <div className="service-row-new">
                  <div className="service-card-new">
                    <h4>Browlift Simple</h4>
                    <div className="price-new">25€</div>
                    <p className="service-description-new">
                      Restructuration naturelle des sourcils avec soins apaisants inclus.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 40 minutes</p>
                      <p>• Épilation précise</p>
                      <p>• Forme personnalisée</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('browlift_simple')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new">
                    <h4>Browlift + Teinture</h4>
                    <div className="price-new">30€</div>
                    <p className="service-description-new">
                      Restructuration complète avec teinture pour des sourcils parfaitement définis.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 40 minutes</p>
                      <p>• Teinture incluse</p>
                      <p>• Effet professionnel</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('browlift_tint')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Restructuration Sourcils */}
              <div className="service-category-new">
                <h3 className="category-title-new">Restructuration Sourcils</h3>
                <div className="service-row-new">
                  <div className="service-card-new">
                    <h4>Restructuration sans épilation simple</h4>
                    <div className="price-new">25€</div>
                    <p className="service-description-new">
                      Mise en forme douce avec teinture pour sublimer vos sourcils naturels.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 30 minutes</p>
                      <p>• Sans épilation</p>
                      <p>• Teinture incluse</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('brow_restruct_no_pluck')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new">
                    <h4>Restructuration sans épilation + teinture</h4>
                    <div className="price-new">30€</div>
                    <p className="service-description-new">
                      Épilation précise et mise en forme complète avec teinture professionnelle.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 30 minutes</p>
                      <p>• Épilation incluse</p>
                      <p>• Résultat optimal</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('brow_restruct_no_pluck_tint')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Nos Packs */}
              <div className="service-category-new">
                <h3 className="category-title-new">Nos Packs</h3>
                <div className="service-row-new">
                  <div className="service-card-new premium-pack-new">
                    <div className="badge-premium-new">PREMIUM</div>
                    <h4>Regard Complet</h4>
                    <div className="price-new">60€</div>
                    <p className="service-description-new">
                      Pack complet avec rehaussement de cils + browlift. Formule tout inclus pour un regard parfait.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Rehaussement + browlift</p>
                      <p>• Teinture offerte</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('pack_lash_brow')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new premium-pack-new">
                    <div className="badge-premium-new">PREMIUM</div>
                    <h4>Sourcils Expert</h4>
                    <div className="price-new">50€</div>
                    <p className="service-description-new">
                      Pack sourcils complet : browlift + restructuration + épilation avec teinture offerte.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Service complet sourcils</p>
                      <p>• Expertise professionnelle</p>
                    </div>
                    <button
                      onClick={() => handleServiceBooking('pack_brow_complete')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  // ANCIENNE Section Galerie supprimée pour éviter conflit avec nouvelle version GitHub
  const _OldGallerySection = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    const categories = [
      { id: 'all', label: 'Toutes' },
      { id: 'lash_lift', label: 'Rehaussement de Cils' },
      { id: 'browlift', label: 'Browlift' },
      { id: 'restructuration', label: 'Restructuration' },
      { id: 'packs', label: 'Packs Complets' }
    ];

    // Images exemple - En production, ces données viendraient d'une API
    useEffect(() => {
      // Simulation d'un chargement de données
      setTimeout(() => {
        setImages([
          {
            id: 1,
            title: "Rehaussement de Cils - Avant/Après",
            description: "Résultat spectaculaire sur cils naturels",
            image_url: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=400",
            category: 'lash_lift'
          },
          {
            id: 2,
            title: "Browlift Transformation",
            description: "Restructuration complète des sourcils",
            image_url: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400",
            category: 'browlift'
          },
          {
            id: 3,
            title: "Pack Regard Complet",
            description: "Cils et sourcils sublimés",
            image_url: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
            category: 'packs'
          },
          {
            id: 4,
            title: "Rehaussement + Teinture",
            description: "Intensité maximale du regard",
            image_url: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400",
            category: 'lash_lift'
          },
          {
            id: 5,
            title: "Restructuration Sourcils",
            description: "Forme parfaite adaptée au visage",
            image_url: "https://images.unsplash.com/photo-1583001931096-959e9a1a6223?w=400",
            category: 'restructuration'
          },
          {
            id: 6,
            title: "Browlift + Teinture",
            description: "Définition parfaite des sourcils",
            image_url: "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400",
            category: 'browlift'
          }
        ]);
        setLoading(false);
      }, 1000);
    }, []);

    const filteredImages = activeCategory === 'all' 
      ? images 
      : images.filter(image => image.category === activeCategory);

    return (
      <div className="container">
        <section className="gallery">
          <div className="section-header">
            <h2>Notre Galerie</h2>
            <p>Découvrez nos réalisations et transformations spectaculaires</p>
          </div>

          {/* Category Filters */}
          <div className="gallery-filters">
            {categories.map(category => (
              <button
                key={category.id}
                className={`gallery-filter ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Gallery Content */}
          {loading ? (
            <div className="gallery-loading">
              <p>Chargement de la galerie...</p>
            </div>
          ) : (
            <div className="gallery-content">
              <div className="gallery-grid-new">
                {filteredImages.map((image) => (
                  <div key={image.id} className="gallery-item-new">
                    <div className="gallery-image-container">
                      <img
                        src={image.image_url}
                        alt={image.title}
                        className="gallery-image"
                      />
                      <div className="gallery-overlay-new">
                        <div className="gallery-overlay-content">
                          <h4>{image.title}</h4>
                          <p>{image.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredImages.length === 0 && (
                <div className="gallery-empty">
                  <p>Aucune réalisation disponible pour cette catégorie.</p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    );
  };

  // FAQ Section Component avec accordéon
  const FAQSection = () => {
    const [activeQuestion, setActiveQuestion] = useState(null);

    const toggleQuestion = (questionId) => {
      setActiveQuestion(activeQuestion === questionId ? null : questionId);
    };

    const faqCategories = [
      {
        title: "Général",
        questions: [
          {
            id: "general-1",
            question: "Qu'est-ce que le rehaussement de cils ?",
            answer: "Le rehaussement de cils est un soin professionnel qui consiste à courber et rehausser vos cils naturels. Cette technique donne l'illusion de cils plus longs et plus fournis sans extension, pour un effet naturel qui dure 6 à 8 semaines."
          },
          {
            id: "general-2",
            question: "Combien de temps dure une séance ?",
            answer: "La durée varie selon le service choisi : 45-60 minutes pour un rehaussement simple, 75 minutes avec teinture, jusqu'à 120 minutes pour nos packs complets."
          },
          {
            id: "general-3",
            question: "À quelle fréquence dois-je renouveler les soins ?",
            answer: "Nous recommandons de renouveler le rehaussement de cils toutes les 6 à 8 semaines, et le browlift toutes les 4 à 6 semaines selon la repousse de vos poils."
          }
        ]
      },
      {
        title: "Avant le soin",
        questions: [
          {
            id: "before-1",
            question: "Comment me préparer avant ma séance ?",
            answer: "Venez sans maquillage sur les yeux, évitez les lentilles de contact le jour J, et informez-nous de toute allergie ou sensibilité particulière lors de la prise de rendez-vous."
          },
          {
            id: "before-2",
            question: "Y a-t-il des contre-indications ?",
            answer: "Les soins sont déconseillés en cas d'infection oculaire, de chirurgie récente des yeux, de grossesse ou d'allaitement. N'hésitez pas à nous consulter en cas de doute."
          },
          {
            id: "before-3",
            question: "Puis-je venir si j'ai des yeux sensibles ?",
            answer: "Oui, nous utilisons des produits hypoallergéniques et adaptons nos techniques aux yeux sensibles. Prévenez-nous lors de la réservation pour que nous puissions prendre toutes les précautions nécessaires."
          }
        ]
      },
      {
        title: "Après le soin",
        questions: [
          {
            id: "after-1",
            question: "Quels sont les soins post-traitement ?",
            answer: "Évitez l'eau, la vapeur et les produits huileux sur les cils pendant 24h. Utilisez une brosse à cils propre pour les discipliner et évitez de les frotter."
          },
          {
            id: "after-2",
            question: "Puis-je me maquiller après le soin ?",
            answer: "Attendez 24h avant d'appliquer du mascara ou tout autre produit sur les yeux. Privilégiez ensuite des produits sans huile pour préserver l'effet du traitement plus longtemps."
          },
          {
            id: "after-3",
            question: "Que faire si j'ai une réaction allergique ?",
            answer: "En cas de rougeur, démangeaison ou gonflement, contactez-nous immédiatement. Nous vous conseillerons sur les mesures à prendre et adapterons vos futurs soins si nécessaire."
          }
        ]
      },
      {
        title: "Tarifs et rendez-vous",
        questions: [
          {
            id: "pricing-1",
            question: "Quels sont vos tarifs ?",
            answer: "Nos tarifs varient de 25€ à 60€ selon le service choisi : Rehaussement simple (30€), avec teinture (35€), Browlift (25-30€), Packs complets (50-60€). Consultez notre section Services pour le détail."
          },
          {
            id: "pricing-2",
            question: "Comment prendre rendez-vous ?",
            answer: "Vous pouvez réserver directement en ligne via notre section 'Réserver', par téléphone, ou par email. Nous confirmons tous les rendez-vous par email avec les détails de votre soin."
          },
          {
            id: "pricing-3",
            question: "Quelle est votre politique d'annulation ?",
            answer: "Nous demandons un préavis de 24h minimum pour toute annulation. Les annulations de dernière minute peuvent faire l'objet de frais selon notre politique tarifaire."
          }
        ]
      }
    ];

    return (
      <div className="container">
        <section className="faq">
          <div className="section-header">
            <h2>Questions Fréquentes</h2>
            <p>Trouvez rapidement les réponses à vos questions sur nos soins et services de beauté du regard. N'hésitez pas à nous contacter si vous ne trouvez pas l'information recherchée.</p>
          </div>

          <div className="faq-categories">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="faq-category">
                <h3 className="faq-category-title">{category.title}</h3>
                <div className="faq-questions">
                  {category.questions.map((faq) => (
                    <div key={faq.id} className="faq-item">
                      <button
                        className={`faq-question ${activeQuestion === faq.id ? 'active' : ''}`}
                        onClick={() => toggleQuestion(faq.id)}
                      >
                        <span>{faq.question}</span>
                        <span className="faq-icon">
                          {activeQuestion === faq.id ? '−' : '+'}
                        </span>
                      </button>
                      {activeQuestion === faq.id && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="faq-contact-section">
            <h3>Vous n'avez pas trouvé votre réponse ?</h3>
            <p>Notre équipe est là pour répondre à toutes vos questions. Contactez-nous par l'un des moyens suivants :</p>

            <div className="faq-contact-methods">
              <div className="contact-method">
                <h4>Instagram</h4>
                <p>mbeauty.off</p>
              </div>
              <div className="contact-method">
                <h4>Email</h4>
                <p>contact@mbeauty.fr</p>
                <span>Réponse sous 24h</span>
              </div>
              <div className="contact-method">
                <h4>Consultation</h4>
                <p>Consultation gratuite</p>
                <span>Sur rendez-vous</span>
              </div>
            </div>

            <div className="faq-contact-actions">
              <button
                onClick={() => setCurrentSection('booking')}
                className="btn btn-primary"
              >
                Nous contacter
              </button>
              <button
                onClick={() => setCurrentSection('booking')}
                className="btn btn-outline"
              >
                Consultation gratuite
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // Gallery Section with categories and tabs
  const GallerySection = () => {
    const [activeCategory, setActiveCategory] = useState('tous');
    const [loading, setLoading] = useState(true);

    // Images professionnelles intégrées directement (pas de BDD)
    const galleryImages = {
      cils: [
        {
          id: 'cils-1',
          title: 'Rehaussement de Cils Naturel',
          image_url: 'https://iili.io/Kfip8Ja.jpg',
          category: 'cils',
          description: 'Regard sublimé et naturel'
        },
        {
          id: 'cils-2',
          title: 'Rehaussement de Cils Naturel',
          image_url: 'https://iili.io/KfipWDQ.jpg',
          category: 'cils',
          description: 'Regard sublimé et naturel'
        }
      ],
      sourcils: [
        {
          id: 'sourcils-1',
          title: 'Restructuration Experte',
          image_url: 'https://iili.io/KfipVOx.jpg',
          category: 'sourcils',
          description: 'Forme parfaitement dessinée'
        },
        {
          id: 'sourcils-2',
          title: 'Restructuration Experte',
          image_url: 'https://iili.io/KfipwiP.jpg',
          category: 'sourcils',
          description: 'Forme parfaitement dessinée'
        },        
        {
          id: 'sourcils-3',
          title: 'Restructuration Experte',
          image_url: 'https://iili.io/Kfs8r9n.jpg',
          category: 'sourcils',
          description: 'Forme parfaitement dessinée'
        }
      ]
    };

    // Get all images from all categories
    const getAllImages = () => {
      return [
        ...galleryImages.cils,
        ...galleryImages.sourcils
      ];
    };

    // Filter images by category
    const getFilteredImages = () => {
      if (activeCategory === 'tous') {
        return getAllImages();
      }
      return galleryImages[activeCategory] || [];
    };

    // Simplification du chargement - les images sont intégrées directement
    useEffect(() => {
      // Plus besoin de chargement depuis la BDD - images intégrées
      setLoading(false);
    }, []);

    const categories = [
      { id: 'tous', label: 'Tous', name: 'Toutes nos réalisations' },
      { id: 'cils', label: 'Cils', name: 'Rehaussement de Cils' },
      { id: 'sourcils', label: 'Sourcils', name: 'Restructuration Sourcils & Browlift' }
    ];

    const filteredImages = getFilteredImages();

    return (
      <div className="container">
        <section className="gallery-new">
          <div className="section-header">
            <h2>Galerie</h2>
            <p>Découvrez nos réalisations et laissez-vous inspirer par la beauté de nos créations.</p>
            <p className="gallery-subtitle">Chaque regard est unique, chaque résultat est une œuvre d'art.</p>
          </div>

          {/* Category Navigation */}
          <div className="gallery-nav">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`gallery-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Gallery Content */}
          {loading ? (
            <div className="gallery-loading">
              <p>Chargement de la galerie...</p>
            </div>
          ) : (
            <div className="gallery-content">
              <div className="gallery-grid-new">
                {filteredImages.map((image) => (
                  <div key={image.id} className="gallery-item-new">
                    <div className="gallery-image-container">
                      <img 
                        src={image.image_url} 
                        alt={image.title}
                        className="gallery-image"
                      />
                      <div className="gallery-overlay-new">
                        <div className="gallery-overlay-content">
                          <h4>{image.title}</h4>
                          <p>{image.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredImages.length === 0 && (
                <div className="gallery-empty">
                  <p>Aucune réalisation disponible pour cette catégorie.</p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    );
  };

  // SECTION SERVICES - Design exact selon l'image fournie
  const ServicesSection = () => {
    // Fonction pour rediriger vers la réservation avec le service pré-sélectionné
    const handleServiceBooking = (serviceType) => {
      // Mise à jour du formulaire avec le service sélectionné
      setFormData(prev => ({
        ...prev,
        service_type: serviceType
      }));
      // Redirection vers la section booking
      setCurrentSection('booking');
    };

    return (
      <div className="container">
        <section className="services">
          <div className="section-header">
            <h2>Nos Services</h2>
            <p>Des soins professionnels pour sublimer votre regard avec nos techniques expertes</p>
          </div>
          
          {loadingServices ? (
            <div className="loading">Chargement des services...</div>
          ) : (
            <div className="services-new-layout">
              {/* Rehaussement de Cils */}
              <div className="service-category-new">
                <h3 className="category-title-new">Rehaussement de Cils</h3>
                <div className="service-row-new">
                  <div className="service-card-new">
                    <h4>Rehaussement Simple</h4>
                    <div className="price-new">30€</div>
                    <p className="service-description-new">
                      Courbure naturelle et ouverture du regard pour des cils parfaitement rehaussés.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Effet longue durée</p>
                      <p>• Technique professionnelle</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('lash_lift')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new popular-card-new">
                    <div className="badge-popular-new">POPULAIRE</div>
                    <h4>Rehaussement + Teinture</h4>
                    <div className="price-new">35€</div>
                    <p className="service-description-new">
                      Courbure parfaite avec intensification de la couleur pour un regard plus intense.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Coloration incluse</p>
                      <p>• Résultat spectaculaire</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('lash_lift_tint')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new premium-card-new">
                    <h4>Formule Complète + Lash Botox</h4>
                    <div className="price-new">40€</div>
                    <p className="service-description-new">
                      Soin complet avec rehaussement, teinture et traitement nourrissant lash botox.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Lash botox inclus</p>
                      <p>• Soin premium</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('lash_lift_tint_botox')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Browlift */}
              <div className="service-category-new">
                <h3 className="category-title-new">Browlift</h3>
                <div className="service-row-new">
                  <div className="service-card-new">
                    <h4>Browlift Simple</h4>
                    <div className="price-new">25€</div>
                    <p className="service-description-new">
                      Restructuration naturelle des sourcils avec soins apaisants inclus.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 40 minutes</p>
                      <p>• Épilation précise</p>
                      <p>• Forme personnalisée</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('browlift_simple')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new">
                    <h4>Browlift + Teinture</h4>
                    <div className="price-new">30€</div>
                    <p className="service-description-new">
                      Restructuration complète avec teinture pour des sourcils parfaitement définis.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 40 minutes</p>
                      <p>• Teinture incluse</p>
                      <p className="hybrid-tint-highlight">✨ <strong>Option teinture hybride (+5€)</strong> <span className="hybrid-tint-star">*</span></p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('browlift_tint')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Restructuration Sourcils */}
              <div className="service-category-new">
                <h3 className="category-title-new">Restructuration Sourcils</h3>
                <div className="service-row-new">
                  <div className="service-card-new">
                    <h4>Restructuration sans épilation simple</h4>
                    <div className="price-new">25€</div>
                    <p className="service-description-new">
                      Mise en forme douce avec teinture pour sublimer vos sourcils naturels.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 30 minutes</p>
                      <p>• Sans épilation</p>
                      <p>• Teinture incluse</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('brow_restruct_no_pluck_tint')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new">
                    <h4>Restructuration sans épilation +  teinture</h4>
                    <div className="price-new">30€</div>
                    <p className="service-description-new">
                      Épilation précise et mise en forme complète avec teinture professionnelle.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 30 minutes</p>
                      <p>• Épilation incluse</p>
                      <p>• Résultat optimal</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('brow_restruct_no_pluck')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Nos Packs */}
              <div className="service-category-new">
                <h3 className="category-title-new">Nos Packs</h3>
                <div className="service-row-new">
                  <div className="service-card-new premium-pack-new">
                    <div className="badge-premium-new">PREMIUM</div>
                    <h4>Regard Complet</h4>
                    <div className="price-new">60€</div>
                    <p className="service-description-new">
                      Pack complet avec rehaussement de cils + browlift. Formule tout inclus pour un regard parfait.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Rehaussement + browlift</p>
                      <p>• Teinture offerte</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('pack_lash_brow')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new premium-pack-new">
                    <div className="badge-premium-new">PREMIUM</div>
                    <h4>Sourcils Expert</h4>
                    <div className="price-new">50€</div>
                    <p className="service-description-new">
                      Pack sourcils complet : browlift + restructuration + épilation avec teinture offerte.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 60 minutes</p>
                      <p>• Service complet sourcils</p>
                      <p>• Expertise professionnelle</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('pack_brow_complete')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>

                  <div className="service-card-new premium-pack-new">
                    <div className="badge-premium-new">PREMIUM</div>
                    <h4>Regard & Sourcils Signature</h4>
                    <div className="price-new">85€</div>
                    <p className="service-description-new">
                      Pack ultime : rehaussement + browlift + restructuration sans épilation, soins et teinture offerts.
                    </p>
                    <div className="service-details-new">
                      <p>• Durée : 90 minutes</p>
                      <p>• Rehaussement + browlift + restructuration</p>
                      <p>• Soins & teinture offerts</p>
                    </div>
                    <button 
                      onClick={() => handleServiceBooking('pack_rehauss_browlift_restruct')}
                      className="service-button-new"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

 

  // Render current section
  const renderSection = () => {
    switch (currentSection) {
      case 'services':
        return <ServicesSection />;
      case 'gallery':
        return <GallerySection />;
      case 'faq':
        return <FAQSection />;
      case 'booking':
        return <BookingSection 
          formData={formData}
          handleInputChange={handleInputChange}
          handleSubmitAppointment={handleSubmitAppointment}
          availableSlots={availableSlots}
          isLoggedIn={isLoggedIn}
          setCurrentSection={setCurrentSection}
          services={services}
          calculateTotalPrice={calculateTotalPrice}
          isSubmitting={bookingSubmitting}
          urgency={urgency}
        />;
      case 'reviews':
        return <ReviewsSection 
          reviewData={reviewForm}
          handleReviewChange={handleReviewInputChange}
          handleReviewSubmit={handleReviewSubmit}
          reviews={reviews}
          services={services}
          isLoggedIn={isLoggedIn}
          setCurrentSection={setCurrentSection}
        />;
      case 'offers':
        return <OffersSection />;
      case 'client-login':
        return <ClientLoginSection 
          loginData={loginForm}
          registerData={registerForm}
          forgotPasswordData={forgotPasswordForm}
          resetPasswordData={resetPasswordForm}
          handleLoginChange={handleLoginInputChange}
          handleRegisterChange={handleRegisterInputChange}
          handleForgotPasswordChange={handleForgotPasswordInputChange}
          handleResetPasswordChange={handleResetPasswordInputChange}
          handleLoginSubmit={handleLogin}
          handleRegisterSubmit={handleRegister}
          handleForgotPasswordSubmit={handleForgotPassword}
          handleResetPasswordSubmit={handleResetPassword}
          showLogin={showLogin}
          setShowLogin={setShowLogin}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          showResetPassword={showResetPassword}
          setShowResetPassword={setShowResetPassword}
        />;
      case 'client-area':
        return isLoggedIn ? <ClientAreaSection /> : <ClientLoginSection 
          loginData={loginForm}
          registerData={registerForm}
          forgotPasswordData={forgotPasswordForm}
          resetPasswordData={resetPasswordForm}
          handleLoginChange={handleLoginInputChange}
          handleRegisterChange={handleRegisterInputChange}
          handleForgotPasswordChange={handleForgotPasswordInputChange}
          handleResetPasswordChange={handleResetPasswordInputChange}
          handleLoginSubmit={handleLogin}
          handleRegisterSubmit={handleRegister}
          handleForgotPasswordSubmit={handleForgotPassword}
          handleResetPasswordSubmit={handleResetPassword}
          showLogin={showLogin}
          setShowLogin={setShowLogin}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          showResetPassword={showResetPassword}
          setShowResetPassword={setShowResetPassword}
        />;
      case 'admin-area':
        return (isLoggedIn && currentClient?.is_admin) ? <AdminAreaSection /> : <ClientLoginSection 
          loginData={loginForm}
          registerData={registerForm}
          forgotPasswordData={forgotPasswordForm}
          resetPasswordData={resetPasswordForm}
          handleLoginChange={handleLoginInputChange}
          handleRegisterChange={handleRegisterInputChange}
          handleForgotPasswordChange={handleForgotPasswordInputChange}
          handleResetPasswordChange={handleResetPasswordInputChange}
          handleLoginSubmit={handleLogin}
          handleRegisterSubmit={handleRegister}
          handleForgotPasswordSubmit={handleForgotPassword}
          handleResetPasswordSubmit={handleResetPassword}
          showLogin={showLogin}
          setShowLogin={setShowLogin}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          showResetPassword={showResetPassword}
          setShowResetPassword={setShowResetPassword}
        />;
      default:
        return <HomeSection />;
    }
  };

  return (
    <div className="App">
      <Toaster />
      <ConfirmDialog />
      <Navigation />
      <main className="main-content">
        {renderSection()}
      </main>
    </div>
  );
};

export default App;